import {useApolloClient} from "@apollo/client";
import {ClientSideRowModelModule} from "@ag-grid-community/client-side-row-model";
import {
  CellClickedEvent,
  ColDef,
  ColumnApi,
  ColumnMovedEvent,
  ColumnVisibleEvent,
  CsvCell,
  CsvExportParams,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  ICellRendererParams,
  IRowDragItem,
  ModuleRegistry,
  RowDragCallbackParams,
  RowDragLeaveEvent,
  RowDragMoveEvent,
  RowSelectedEvent,
  SideBarDef,
  SortChangedEvent
} from "@ag-grid-community/core";
import {CsvExportModule} from "@ag-grid-community/csv-export";
import {AgGridReact} from "@ag-grid-community/react";
import {MenuModule} from "@ag-grid-enterprise/menu";
import {Button, Icon, Intent, Popover, Position, Text, Tooltip} from "@blueprintjs/core";
import {solid, thin} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Loader} from "@googlemaps/js-api-loader";
import {format} from "date-fns";
import React, {Dispatch, ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useAppContext} from "../../../ApplicationContext";
import {useAuthState} from "../../../providers/AuthProvider";
import {
  Driver,
  Eta,
  EtaItemInput,
  JobStopManifestSequence,
  Manifest,
  OrderRequirement,
  RouteOptimizationActivity,
  RouteOptimizationRequest,
  RouteOptimizationRequestOptionType,
  RouteOptimizationResponse,
  RouteOptimizationResultItem,
  RouteOptimizationUnassignedDetail,
  SaveClearJobStop,
  SearchableDispatchGroupSortableFields,
  SearchableSortDirection,
  Skill,
  useCloseManifestMutation,
  useOptimizeRoutesLazyQuery,
  useReassignJobStopsMutation,
  useResequenceManifestStopsMutation,
  useSaveClearJobStopsMutation,
  useSearchDispatchGroupsQuery,
  useSearchDriverLocationQuery,
  useSearchDriversEnhancedQuery,
  useSearchVehicleTypesQuery,
  useSetUserPrefMutation,
  useSplitJobMutation,
  useUnassignJobStopsFromManifestMutation,
  useGetEtaQuery,
  useUpdateManifestMutation,
  VehicleType
} from "../../../generated/graphql";
import {useBatchOptimization} from "../../../hooks/useBatchOptimization";
import {useFeatureFlagContext} from "../../../providers/FeatureFlagProvider";
import {EverCompletedCheckDialog} from "../../common/EverCompletedCheckDialog";
import {useJobsEverCompleted} from "../../../hooks/useJobsEverCompleted";
import {useSnoozeIndicator} from "../../../hooks/useSnoozeIndicator";
import {
  createJsonPref,
  extractJsonPref,
  extractSimplePref,
  PrefValue,
  usePreferenceContext
} from "../../../providers/PreferenceProvider";
import {
  addOptimizedSequenceToStops,
  isActiveOrQueued,
  isCompleted,
  isJobAlreadySplit,
  JobStopStatus,
  JobStopType,
  jobStopTypeToString
} from "../../../utils/stops/StopUtils";
import {buildAdjustStopCalls, buildAdjustToastMessages} from "../../../utils/stops/adjustStopTime.utils";
import {getDateTimeAtSpecificTimeZone, getLocalDateTime} from "../../../utils/DateTimeUtils";
import {
  addressSort,
  copyToClipboard,
  getArrayFromField,
  getTimeZoneIdByLatLng,
  groupDetailsByCode
} from "../../../utils/General";
import {getMaxStopCount, hasOpenStops, hasTooManyStops} from "../../../utils/ManifestUtils";
import {
  AppToaster,
  AppToasterTopLeft,
  showSplitJobErrorToast,
  showSplitJobToastSuccess
} from "../../../utils/toasters/toaster";
import {showToastWithTitle} from "../../../utils/toasters/ToasterWithTitle";
import {CellRendererWithToolTip} from "../../common/cell-renderers/CellRendererWithToolTip";
import {ContactCellRenderer, ContactValueGetter} from "../../common/cell-renderers/ContactCellRenderer";
import {DetailsCellRenderer} from "../../common/cell-renderers/DetailsCellRenderer";
import DoubleFieldCellRenderer, {DoubleFieldCellValue} from "../../common/cell-renderers/DoubleFieldCellRenderer";
import AdjustPaymentBody from "./components/AdjustPaymentBody";
import {
  ColoredJobNumberCellRenderer,
  ManifestJobColorPalette
} from "../../common/cell-renderers/ColoredJobNumberCellRenderer";
import {JobPiecesOrWeightCellRenderer} from "../../common/cell-renderers/JobPiecesAndWeightCellRenderer";
import {
  DateTimeCellRenderer,
  getDateTimeByType,
  getStopDateTimeCellValue,
  StopDateTimeType
} from "../../common/cell-renderers/DateTimeCellRenderer";
import {ConfirmationDialog} from "../../common/ConfirmationDialog";
import ActiveFilterDialog, {IActiveFilterDialogRef} from "../../common/ActiveFilterDialog";
import {
  Constants,
  DateFormats,
  DEFAULT_POPOVER_MODIFIERS,
  EmptyValueStrings,
  OPTIMIZATION_LOCATION_TYPES,
  OptimizedResultCords,
  SaveAndClearJobStopDateTimeSource,
  TenantPreferences,
  UserPreferences
} from "../../../constants/constants";
import DateFilter from "../../common/DateFilter";
import {useDispatchRulesDataContext} from "../../../providers/DispatchRulesDataProvider";
import {getColorPriority} from "../../job/JobPanel";
import decodePolyLine from "../../map/DecodePolyLine";
import ManifestDetailsMap from "../../map/manifest-details/ManifestDetailsMap";
import {ConditionNames} from "../../settings/ColorSettings/ColorizedIndicators/RuleGenerator.service";
import {ManifestStatusType} from "../../settings/ColorSettings/ColorizedIndicators/types/manifestAttributes";
import DispatchGroupsSeletor, {TGroupOption} from "../common/components/DispatchGroupsSeletor";
import DriverQualV3 from "../common/components/cards/common/components/DriverQual";
import {getManifestColors} from "../list/components/ManifestDriverList";
import {getManifestOnlyColor} from "../list/components/ManifestDriverList.service";
import {getHighestRankedColor, StopColor} from "../common/components/cards/common/components/ManifestCardCommonV2";
import ManifestStatusSeletor from "../common/components/ManifestStatusSeletor";
import StopCountIndicator from "../common/components/StopCountIndicator";
import {decorateStopIndicatorColors} from "../../../utils/color-indicator/color-indicator.utils";
import {useDispatchGroupContext} from "../../../providers/DispatchGroupProvider";
import {getFormattedManifestDate} from "../common/utils/manifest-date";
import {fetchDispatchNotes} from "../../../services/DispatchNotesLookupService";
import AdjustStopTime from "./components/AdjustStopTime";
import CompleteStopDialog from "./components/CompleteStopDialog";
import DragCellRenderer from "./components/DragCellRenderer";
import ErrorDialog, {ErrorDialogRefs} from "./components/ErrorDialog";
import ETAColHeaderRenderer from "./components/ETAColHeaderRenderer";
import JobColHeaderRenderer from "./components/JobColHeaderRenderer";
import {ManifestDetailsContextMenu} from "./components/ManifestDetailsContextMenu";
import {PendingSnooze, SnoozeConfirmationDialog} from "./components/SnoozeConfirmationDialog";
import ManifestDetailsNoRowsOverlay from "./components/ManifestDetailsNoRowsOverlay";
import OptimizeDialog, {OptimizeFormData} from "./components/OptimizeDialog";
import OptimizedStatus from "./components/OptimizedStatus";
import {getSiteConfig} from "../../../services/TenantService";
import {OptimizeSpiner, OptimizeSpinerRefs, SIZE_OF_OPTIMIZED_SPINNER} from "./components/OptimizeSpiner";
import {ServiceTypeFilter} from "./components/ServiceFilter";
import {TimeWindowErrorDialog, TimeWindowErrorDialogRefs} from "./components/TimeWindowErrorDialog";
import {
  ButtonsHeaderContainer,
  CancelButton,
  CancelButtonText,
  CancelReassignmentButton,
  ClickHereButton,
  CloseManifestToolTip,
  DetailButton,
  DriverCode,
  GridContainer,
  GridHeaderContainer,
  ManifestCardItemContainer,
  ManifestDateAndConfigButton,
  ManifestDateContainer,
  ManifestDateContainerChangesPending,
  ManifestDriverDetailsContainer,
  ManifestDriverDetailsToolsContainer,
  ManifestDriverDetailsWrapper,
  ManifestToolbarActionButton,
  ManifestToolbarButton,
  PipeIcon,
  SaveChangesButton,
  SaveChangesContainer,
  StopsGrid,
  ToolbarButtonsContainer,
  WindowButtonsContainer
} from "./ManifestDetail.styles";
import "./ManifestDetails.css";
import {
  checkNoSpaceBetweenSelectedRows,
  checkRowMoveAndDoFunction,
  DateValueGetter,
  extractJobStopIdFromActivity,
  filterInvalidAddresses,
  filterInvalidStartEndLocation,
  compareIndicatorColumn,
  findNotCompletedStops,
  getActiveDriverSkills,
  getDraggingStopsList,
  getEtaSourcesDirty,
  getManifestWrongOrderJobNumbers,
  getNotificationWindowCellValue,
  getNotificationWindowExportValue,
  handleScrollTop,
  isHideETA,
  isStopsSequenceChanged,
  jobStopStatusValueFormatter,
  applySnoozedStops,
  SnoozedStops
} from "./utils/ManifestDetails.utils";
import {ManifestDetailsChangeContext} from "./ManifestDetailsChangeEventProvider";
import {DetailViews} from "./ManifestDetailsContainer";
import {ManifestDetailsManifestSelectorProps} from "./ManifestDetailsManifestSelector";
import {ManifestDetailsDriverSelectorProps} from "./ManifestDetailsDriverSelector";
import {useAssignmentViewState} from "../../../providers/AssignmentViewProvider";
import {ManifestDetailsAssignmentActions, ManifestDetailsAssignmentState} from "./ManifestDetailsReducer";
import {
  excludeTimeWindowErrors,
  extractAddressFromMessage,
  findJobsWithNewStatusStops
} from "./ManifestDetailsV2.service";
import StopStatusRenderer from "./StopStatusRenderer";
import {JobNumberTooltipUsage} from "../../common/cell-renderers/JobNumberCellRenderer";
import {ETAServiceStatus} from "./components/ETAServiceStatus";
import {useOktaAuth} from "@okta/okta-react";
import {CloseManifestDialog, CloseManifestDialogRef} from "./components/CloseManifestDialog";
import {OptimizationLocation, TOptimizeErrorType} from "./ManifestDetails.type";
import log from "loglevel";
import {SwapIcon} from "../../common/icons";
import VehicleSelector from "../common/components/VehicleSelector";
import {useDispatchDataContext} from "../../../providers/DispatchDataProvider";
import {useMapVisibilityDispatch, useMapVisibilityState} from "../../map/MapVisibilityContext";
import {useManifestDetailsDispatch} from "../../../providers/ManifestDetailsProvider";
import {useAssignmentDispatch, useAssignmentState} from "../../../providers/AssignmentProdiver";
import {
  AG_GRID_NOT_EXIST_FILTER,
  clearAllSort,
  getOrderSkillFilterParams,
  orderSkillsValueGetter
} from "../../../utils/ag-grid/AGGridUtils";
import {debounce, isEmpty, isEqual, set, uniq, uniqBy} from "lodash";
import {StopTypeIconCellRenderer} from "../../common/cell-renderers/StopTypeIconCellRenderer";
import {ColorIndicatorCellRenderer} from "../../common/cell-renderers/ColorIndicatorCellRenderer";
import ServiceTypeCellRenderer from "../../common/cell-renderers/ServiceTypeCellRenderer";
import {ETASource} from "./enums/enums";
import {ETACellRenderer} from "../../common/cell-renderers/ETACellRenderer";
import {TManifestEnhanced, TManifestStopEnhanced} from "../../common/types";
import {JobRequirement} from "../../../providers/AssignmentReducer";
import {AssignJobsConfirmation} from "./components/AssignJobsConfirmation";
import {DriverRequirementEnforcementLevel} from "../../settings/enums/DriverRequirementEnforcementLevel";
import {AssignmentBarManifestDetails} from "../../job/components/AssignmentBarV2";
import {DollarIcon} from "../../common/icons/DollarIcon";
import {
  buildSkillOverrideNotesFromStops,
  getMatchRequirementManifests
} from "../../../utils/assignment/Assignment.utils";
import {JobLinksRenderer, TJobLinkItem} from "../../common/JobLinksRenderer";
import {TDriverEnhanced} from "../../common/types/enhanced";

ModuleRegistry.registerModules([ClientSideRowModelModule, MenuModule, CsvExportModule]);

export type GraphHopperInvalidStopMess = {
  stopId: number;
  address: Promise<any> | string;
  message: string;
};
export type GraphHopperValidStopMess = {
  stopId: number;
  address: string;
} & RouteOptimizationUnassignedDetail;

export type GraphHopperInvalidLocationMess = {
  message: string;
  address: string;
  serviceId: string;
};

type ManifestDetailsProps = {
  manifestDriverId: number;
  manifestDetailsAssignmentState: ManifestDetailsAssignmentState;
  manifestDetailsAssignmentDispatch: Dispatch<ManifestDetailsAssignmentActions>;
  onClose(): void;
  detailViewType: DetailViews;
  assignmentManifestList: ReactElement<ManifestDetailsManifestSelectorProps>;
  splitDriverList?: ReactElement<ManifestDetailsDriverSelectorProps>;
  isTestingEnvironment?: boolean;
};

type HandleUpdateStopCallProps = {
  manifest: Manifest;
  updateJobStopVariables: SaveClearJobStop[];
  jobStopId?: number;
  callType: UpdateStopCallType;
  finallyCallback: () => void;
};

enum UpdateStopCallType {
  COMPLETE = "Complete",
  INCOMPLETE = "Incomplete"
}

type UnassignJobState = {
  jobStopIds: number[];
  jobNumbers: string[];
  jobIds: number[];
};

// Context for Manifest Details context menu
export type ManifestDetailsContext = {
  selectedStops: TManifestStopEnhanced[];
  manifest: TManifestEnhanced;
  handleUnassignJob({jobStopIds, jobNumbers}: UnassignJobState): void;
  handleMarkJobStopComplete(jobStopId: number): void;
  handleMarkJobStopIncomplete(jobStopId: number): void;
  handleReassignJob(singleSelectedJob?: TManifestStopEnhanced): void;
  handleAdjustTimeJob(stops: TManifestStopEnhanced[]): void;
  handleSplitJob(jobId: number): void;
  canUnassignPartialJob: boolean;
  isSplitJobEnabled: boolean;
  // DP-3070: the snooze menu item shows only when the flag is on and the handler is set. The handler
  // gets every stop the action targets (a multi-selection, or the right-clicked row).
  backendColorRulesEnabled?: boolean;
  handleSnoozeIndicator?: (stops: TManifestStopEnhanced[], minutes: number) => void;
};

export type TOptimizedInfo = {
  savedTime: number;
  savedDistance: number;
  totalDistance: number;
  estimatedCompletionDateTime: string;
};

export type AdjustTimeState = Pick<SaveClearJobStop, "adjustScheduledMinutes" | "adjustLateMinutes">;

enum DialogType {
  pendingSequences,
  wrongOrder,
  normalUnassign,
  partialUnassign,
  reassignJob,
  exportConfirm,
  routeOptimization
}

export const getDragIconTooltipContent = ({
  isSorting,
  canResequenceByFilter,
  isSelectRowUncontinuously,
  params,
  clearAllSelection
}: {
  isSorting: boolean;
  canResequenceByFilter: boolean;
  isSelectRowUncontinuously: boolean;
  params: ICellRendererParams<TManifestStopEnhanced>;
  clearAllSelection: () => void;
}) => {
  const reasons = [];
  const actions = [];

  if (isSorting) {
    reasons.push("sorting");
    actions.push("sorting");
  }
  if (!canResequenceByFilter) {
    reasons.push("not filtering by both  Active and Queued status");
    actions.push("filtering");
  }
  if (!isSelectRowUncontinuously) {
    reasons.push("selecting stops that are not sequential");
    actions.push("selected stops");
  }

  const handleClearGridState = () => {
    if (!canResequenceByFilter) {
      params.api.setFilterModel(null);
    }
    clearAllSort(params);
    clearAllSelection();
  };

  return (
    <div style={{padding: "4px", maxWidth: "550px"}}>
      <span>Resequencing is disabled when {reasons.join(", ")}</span>
      <div>
        <ClickHereButton data-testid="clear-grid-state-button" onClick={handleClearGridState}>
          Click here
        </ClickHereButton>
        <span> to clear {actions.join(", ")}</span>
      </div>
    </div>
  );
};

const UnExportedColumnIds = ["indicatorColor", "dragColumn", "details"];

/**
 * Ensures newly added columns are present in saved column state for users
 * whose preferences were saved before the column existed.
 * Each entry specifies the new colId, the colId it should appear after, and defaults.
 */
const NEW_COLUMN_BACKFILLS: {colId: string; afterColId: string; defaults: Record<string, unknown>}[] = [
  {colId: "notificationWindowSent", afterColId: "etaGeneratedDateTime", defaults: {hide: true, width: 220}},
  // DP-3156: Job Alias has to sit beside Order Alias. applyColumnState({applyOrder: true})
  // only positions colIds present in the saved state, so without this it lands at the far
  // right for anyone whose layout predates the column.
  {colId: "job.alias", afterColId: "order.alias", defaults: {hide: true, width: 90}}
];

const backfillNewColumns = (columnState: any[]): any[] => {
  for (const {colId, afterColId, defaults} of NEW_COLUMN_BACKFILLS) {
    if (!columnState.some((item) => item.colId === colId)) {
      const afterIndex = columnState.findIndex((item) => item.colId === afterColId);
      if (afterIndex > -1) {
        columnState.splice(afterIndex + 1, 0, {colId, ...defaults});
      }
    }
  }
  return columnState;
};

const loader = new Loader({
  apiKey: Constants.GOOGLE_API_KEY
});
loader.importLibrary("places").then();

const ManifestDetailsV2 = ({
  manifestDriverId,
  manifestDetailsAssignmentState,
  manifestDetailsAssignmentDispatch,
  onClose,
  assignmentManifestList,
  splitDriverList,
  isTestingEnvironment = false
}: ManifestDetailsProps) => {
  const {appState} = useAppContext();
  const apolloClient = useApolloClient();
  const authState = useAuthState();
  const {oktaAuth} = useOktaAuth();
  const {
    driverRequirements: driverRequirementsEnabled,
    manifestOptimization: manifestOptimizationFeature,
    etas: isETAEnabled,
    askManifestCheckoutInfo: askManifestCheckoutInfoEnabled,
    skills: skillsEnabled,
    multipleSort: multipleSortEnabled,
    dispatchNote: dispatchNotesEnabled,
    etaFromOptimization: isEtaFromOptimizationEnabled,
    splitJob: isSplitJobEnabled,
    routeOptimizationBatchModeStops,
    backendColorRules: backendColorRulesEnabled
  } = useFeatureFlagContext();

  // DP-3070: stops snoozed from this drawer, shown uncoloured through an overlay on the rows and the
  // header (see applySnoozedStops), and the snooze waiting for confirmation.
  const [snoozedStops, setSnoozedStops] = useState<SnoozedStops>({});
  const [pendingSnooze, setPendingSnooze] = useState<PendingSnooze | undefined>(undefined);
  useEffect(() => {
    setSnoozedStops({});
    setPendingSnooze(undefined);
  }, [manifestDriverId]);
  const handleStopSnoozed = useCallback(
    (jobStopIds: number[], snoozeUntilIso: string) =>
      setSnoozedStops((previous) => ({
        ...previous,
        ...Object.fromEntries(jobStopIds.map((jobStopId) => [jobStopId, snoozeUntilIso]))
      })),
    []
  );
  const {snooze} = useSnoozeIndicator({manifestDriverId, onSnoozed: handleStopSnoozed});
  // The context menu only picks the duration; the snooze runs once SnoozeConfirmationDialog confirms.
  const handleSnoozeIndicator = useCallback(
    (stops: TManifestStopEnhanced[], minutes: number) => setPendingSnooze({stops, minutes}),
    []
  );
  const handleSnoozeCancelled = useCallback(() => setPendingSnooze(undefined), []);

  const [unassignJob] = useUnassignJobStopsFromManifestMutation();
  const [updateJobStop] = useSaveClearJobStopsMutation();
  const [setUserPref] = useSetUserPrefMutation();
  const [closeManifest] = useCloseManifestMutation();
  const [resequenceManifestStops] = useResequenceManifestStopsMutation();
  const [updateManifestMutation] = useUpdateManifestMutation();
  const [splitJobMutation, {loading: splitJobLoading}] = useSplitJobMutation();
  const [optimizeRoutes] = useOptimizeRoutesLazyQuery({});
  const {
    data: ETAData,
    refetch: ETArefetch,
    loading: etaLoading
  } = useGetEtaQuery({
    variables: {manifestDriverIds: [manifestDriverId], includeLastSent: true},
    skip: !isETAEnabled || !manifestDriverId,
    pollInterval: Constants.GENERAL_POLLING_INTERVAL
  });
  const dataChangedState = useContext(ManifestDetailsChangeContext);

  useSearchDriversEnhancedQuery({
    variables: {
      filter: {
        driver: {
          driverId: {eq: manifestDetailsAssignmentState.selectedManifest?.driver.driverId}
        }
      }
    },
    onCompleted(data) {
      if (data.searchDriversEnhanced?.items?.at(0)) setDriverInfo(data.searchDriversEnhanced?.items?.at(0) as Driver);
    },
    skip: !manifestDetailsAssignmentState.selectedManifest?.driver.driverId
  });

  useSearchDispatchGroupsQuery({
    variables: {
      filter: {
        dispatchType: {
          eq: "D"
        }
      },
      sort: {
        field: SearchableDispatchGroupSortableFields.DispatchGroup,
        direction: SearchableSortDirection.Asc
      },
      limit: Constants.DISPATCH_GROUP_LIMIT
    },
    onCompleted(data) {
      if (data.searchDispatchGroups?.items) {
        const groupOptions: TGroupOption[] = data.searchDispatchGroups.items.map((item) => {
          return {key: item?.dispatchGroupId as number, value: item?.dispatchGroup ?? EmptyValueStrings.unknown};
        });
        setDispatchGroupOptions(groupOptions);
      }
    }
  });

  const vehicleTypesResults = useSearchVehicleTypesQuery({
    variables: {
      limit: 1000
    }
  });

  const driverLocationResult = useSearchDriverLocationQuery({
    pollInterval: 15000,
    variables: {
      filter: {
        driverIds: [manifestDetailsAssignmentState.selectedManifestDriver?.driverId ?? 0]
      },
      limit: Constants.DRIVER_LOCATION_LIMIT
    }
  });
  const ruleContextState = useDispatchRulesDataContext();
  const mapVisibilityState = useMapVisibilityState();
  const mapVisibilityDispatch = useMapVisibilityDispatch();
  const manifestDetailsDispatch = useManifestDetailsDispatch();
  const {selectedStops, selectedManifest, splitJobDriver} = useAssignmentState();
  const assignmentDispatch = useAssignmentDispatch();
  const {manifestFilters: dashboardFilters} = useAssignmentViewState();
  const {skills, orderRequirements, users} = useDispatchDataContext();

  const gridRef = useRef<AgGridReact<TManifestStopEnhanced>>(null);
  const activeFilterDialogRef = useRef<IActiveFilterDialogRef>(null);
  const jobColoringIndex = useMemo<Map<string, number>>(() => {
    const coloringIndex = new Map<string, number>();
    const jobNumbers = uniq(manifestDetailsAssignmentState.selectedManifest?.stops?.map((s) => s.job.jobNumber));
    if (jobNumbers.length <= ManifestJobColorPalette.length) {
      jobNumbers.forEach((value: string, index: number) => coloringIndex.set(value, index));
    }
    return coloringIndex;
  }, [manifestDetailsAssignmentState.selectedManifest?.stops]);
  const [unassignJobState, setUnassignJobState] = useState<UnassignJobState | undefined>(undefined);
  const [markCompletedJobStopId, setMarkCompletedJobStopId] = useState<number | undefined>(undefined);
  const [markIncompleteJobStopId, setMarkIncompleteJobStopId] = useState<number | undefined>(undefined);
  const [adjustStops, setAdjustStops] = useState<TManifestStopEnhanced[] | undefined>(undefined);
  const [workingStops, setWorkingStops] = useState<TManifestStopEnhanced[]>([]);
  // DP-3070: what the grid shows. Derived here rather than written into workingStops or
  // selectedManifest, so a snooze never disturbs drag, optimise or the 15 s rebuild.
  const displayedStops = useMemo(
    () => applySnoozedStops(workingStops, snoozedStops, Date.now()),
    [workingStops, snoozedStops]
  );
  const [isOptimized, setIsOptimized] = useState<boolean>(false);
  const [isDraggedStops, setIsDraggedStops] = useState<boolean>(false);
  const [optimizationEtas, setOptimizationEtas] = useState<EtaItemInput[]>([]);
  const [canResequenceByFilter, setCanResequenceByFilter] = useState<boolean>(true);
  const [isSorting, setIsSorting] = useState<boolean>(false);

  const [initializing, setInitializing] = useState<boolean>(true);
  const {userPreferences, userPrefsQueryRefetch, tenantPreferences, isUserPreferencesLoaded} = usePreferenceContext();
  const [gridReady, setGridReady] = useState<boolean>(false);
  const [hasPendingSequenceUpdates, setHasPendingSequenceUpdates] = useState<boolean>(false);
  const [etaSourcesDirty, setEtaSourcesDirty] = useState<ETASource[]>([]);
  const [hasPendingSeqUpdatesAlertVisible, setHasPendingSeqUpdatesAlertVisible] = useState<boolean>(false);
  const [isWrongOrderAlertVisible, setIsWrongOrderAlertVisible] = useState<boolean>(false);
  const [isAutoCloseManifest, setIsAutoCloseManifest] = useState<boolean>(false);
  const [wrongOrderJobs, setWrongOrderJobs] = useState<string[]>([]);
  const [dialogOptimizeRouteOpen, setDialogOptimizeRouteOpen] = useState(false);
  const [optimizeFormData, setOptimizeFormData] = useState<OptimizeFormData>();
  const [typeOptimizeErrors, setTypeOptimizeErrors] = useState<TOptimizeErrorType>("");
  const [newStatusJobs, setNewStatusJobs] = useState<TJobLinkItem[]>([]);
  const [invalidAddresses, setInvalidAddresses] = useState<OptimizationLocation[]>([]);
  const [graphHopperInvalidStopMessages, setGraphHopperInvalidStopMessages] = useState<GraphHopperInvalidStopMess[]>(
    []
  );
  const [graphHopperValidStopMessages, setGraphHopperValidStopMessages] = useState<GraphHopperValidStopMess[][]>([]);
  const [graphHopperGeneticMess, setGraphhopperGeneticMessages] = useState<string>("");
  const [graphHopperInvalidLocationMess, setGraphHopperInvalidLocationMess] = useState<
    GraphHopperInvalidLocationMess[]
  >([]);
  const optimizeSpinerRef = useRef<OptimizeSpinerRefs>(null);
  const errorDialogRef = useRef<ErrorDialogRefs>(null);
  const timeWindowErrorDialogRef = useRef<TimeWindowErrorDialogRefs>(null);
  const closeManifestDialogRef = useRef<CloseManifestDialogRef | null>(null);

  const [openReassignJobConfirmation, setOpenReassignJobConfirmation] = useState<boolean>(false);
  const [openSplitJobConfirmation, setOpenSplitJobConfirmation] = useState<boolean>(false);
  const [openCancelSplitJobConfirmation, setOpenCancelSplitJobConfirmation] = useState<boolean>(false);
  const [driverSplitPercentage, setDriverSplitPercentage] = useState<string | undefined>(undefined);
  const [reassignJobStops] = useReassignJobStopsMutation();
  const [isEnableQualified, setEnableQualified] = useState<boolean>(false);
  const [showReassignJobConfirmation, setShowWarningReassignJob] = useState(false);
  const [isOutsideAgGrid, setIsOutsideAgGrid] = useState(false);
  const [rowDataWhenStartedDragging, setRowDataWhenStartedDragging] = useState<TManifestStopEnhanced[]>([]);
  const isTimezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false)
    .value as boolean;
  const [isOpenDispatchGroupSelector, setIsOpenDispatchGroupSelector] = useState<boolean>(false);
  const [dispatchGroupOptions, setDispatchGroupOptions] = useState<TGroupOption[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<TGroupOption | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [selectedVehicleTypeId, setSelectedVehicleTypeId] = useState<number | undefined>(undefined);
  const [driverInfo, setDriverInfo] = useState<TDriverEnhanced | undefined>(undefined);
  const [openNormalUnassignDialog, setOpenNormalUnassignDialog] = useState<boolean>(false);
  const [openPartialUnassignDialog, setOpenPartialUnassignDialog] = useState<boolean>(false);
  const [isOpenExportConfirmDialog, setIsOpenExportConfirmDialog] = useState<boolean>(false);
  const [showEverCompletedDialog, setShowEverCompletedDialog] = useState(false);
  const [everCompletedJobs, setEverCompletedJobs] = useState<
    Array<{jobId: number; jobNumber: string; orderId?: number}>
  >([]);
  const [currentActionText, setCurrentActionText] = useState<string>("assigned");

  // Batch optimization state
  const optimizeDateTimeRef = useRef<Date>();
  const [optimizedInfo, setOptimizedInfo] = useState<TOptimizedInfo | undefined>(undefined);
  const [optimizedResultCords, setOptimizedResultCords] = useState<Map<
    OptimizedResultCords,
    Array<{lat: number; lng: number}>
  > | null>(null);
  const [isOptimizeFromHomeLocation, setIsOptimizeFromHomeLocation] = useState<boolean>(false);

  const [isFirstDataRendered, setIsFirstDataRendered] = useState<boolean>(false);
  const [highestRankIndicatorColor, setHighestRankIndicatorColor] = useState("");
  // DP-3292: processHighestRankColor's backend branch now awaits before it sets state, so a
  // run can land after the drawer closed or after a newer run started — it is called from
  // three places (the 15s poll, the ETA-arrival effect and the sequence check), none of which
  // can pass a cancellation token. A generation claimed on entry and re-checked after the
  // await makes the latest run win and drops superseded ones.
  const highestRankColorRunRef = useRef(0);

  const canUnassignPartialJob = useMemo(() => {
    return extractJsonPref(tenantPreferences, TenantPreferences.unassignPartialJob, false).value.value;
  }, [tenantPreferences]);

  const isUseETAService = useMemo(() => {
    return extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;
  }, [tenantPreferences]);

  const manifestETA =
    isETAEnabled && isUseETAService
      ? ETAData?.getETA?.items?.find((item) => item.manifestDriverId === manifestDriverId)
      : undefined;
  const manifestETARef = useRef(manifestETA);
  manifestETARef.current = manifestETA;

  // Original stops with ETA applied — stable reference for comparisons like getEtaSourcesDirty
  const originalStopsWithETA = useMemo(() => {
    const stops = manifestDetailsAssignmentState.selectedManifest?.stops;
    if (!stops || !manifestETA) return stops;
    return stops.map((stop) => {
      const etaItem =
        manifestETA.message === "Success"
          ? manifestETA.items?.find((item) => item.jobStopId === stop.jobStopId)
          : undefined;
      return {...stop, eta: etaItem?.eta, etaSource: etaItem?.etaSource};
    });
  }, [manifestDetailsAssignmentState.selectedManifest?.stops, manifestETA]);

  const applyETAToStops = useCallback((stops: TManifestStopEnhanced[]) => {
    const eta = manifestETARef.current;
    stops.forEach((stop) => {
      const etaItem =
        eta?.message === "Success" ? eta?.items?.find((item) => item.jobStopId === stop.jobStopId) : undefined;
      stop.eta = etaItem?.eta;
      stop.generatedDateTime = etaItem?.generatedDateTime;
      stop.etaSource = etaItem?.etaSource;
      stop.notificationSentData = eta?.lastSentItems?.find((item) => item.jobStopId === stop.jobStopId);
    });
  }, []);

  const askCheckoutTimeAndMilage = useMemo(() => {
    return extractSimplePref(tenantPreferences, TenantPreferences.askCheckoutTimeAndMilage, false).value;
  }, [tenantPreferences]);

  const isSkillsBasedRequirements = extractSimplePref(
    tenantPreferences,
    TenantPreferences.skillsBasedRequirements,
    false
  ).value as boolean;

  const isSkillsBased = useMemo(() => {
    return isSkillsBasedRequirements && skillsEnabled;
  }, [isSkillsBasedRequirements, skillsEnabled]);

  const {getEverCompletedJobs} = useJobsEverCompleted();

  const {driverGroups} = useDispatchGroupContext();
  const driverRequiredSetting = useMemo(() => {
    const prefVal: PrefValue<DriverRequirementEnforcementLevel> = extractSimplePref(
      tenantPreferences,
      TenantPreferences.driverRequirementEnforcement,
      DriverRequirementEnforcementLevel.Allow
    );
    return prefVal.value as DriverRequirementEnforcementLevel;
  }, [tenantPreferences]);

  const mappedDriverInfo = useMemo(() => {
    if (!driverInfo) return undefined;
    const group = driverGroups.find((g) => g.dispatchGroupId === driverInfo.dispatchGroupId);
    return {
      ...driverInfo,
      dispatchGroup: group || driverInfo.dispatchGroup
    };
  }, [driverInfo, driverGroups]);

  const renderUnassignJobIdsText = useMemo(() => {
    return unassignJobState && unassignJobState.jobNumbers.length > 1
      ? `${[...new Set(unassignJobState.jobNumbers)].join(", ")}`
      : unassignJobState?.jobNumbers.at(0) ?? "";
  }, [unassignJobState]);

  const renderReassignJobText = useMemo(() => {
    return selectedStops.length > 1
      ? `${[...new Set(selectedStops.map((s) => s.job.jobNumber))].join(", ")}`
      : selectedStops[0]?.job?.jobNumber ?? "";
  }, [selectedStops]);

  const selectedJobStopIds = useMemo(() => {
    return selectedStops.map((x) => x.jobStopId);
  }, [selectedStops]);

  const isSelectRowUncontinuously = useMemo(() => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes() ?? [];
    const sortedRowIndex = selectedNodes
      .map((node) => node.rowIndex)
      .filter((index): index is number => index !== null)
      .sort((a, b) => a - b);
    return checkNoSpaceBetweenSelectedRows(sortedRowIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStops]);

  const isAllowResequence = useMemo(
    () => !isSorting && canResequenceByFilter && isSelectRowUncontinuously,
    [canResequenceByFilter, isSelectRowUncontinuously, isSorting]
  );

  const columnTypes: {[key: string]: ColDef<TManifestStopEnhanced>} = useMemo(() => {
    return {
      filterColumn: {
        suppressMenu: false,
        menuTabs: ["filterMenuTab"],
        filterParams: {
          buttons: ["apply", "reset"],
          closeOnApply: true
        }
      },
      textFilter: {
        filter: "agTextColumnFilter"
      },
      numberFilter: {
        filter: "agNumberColumnFilter",
        filterParams: {
          filterOptions: [
            "equals",
            "notEqual",
            "lessThan",
            "lessThanOrEqual",
            "greaterThan",
            "greaterThanOrEqual",
            "inRange"
          ]
        }
      },
      dateFilter: {
        filter: DateFilter
      },
      setFilter: {
        filter: "agSetColumnFilter"
      }
    };
  }, []);

  const clearAllSelection = useCallback(() => {
    gridRef.current?.api.deselectAll();
    assignmentDispatch({type: "ClearSelections"});
  }, [assignmentDispatch]);

  // DP-3070: confirming a snooze clears the selection, as unassign and a saved resequence do.
  const handleSnoozeConfirmed = useCallback(() => {
    const pending = pendingSnooze;
    setPendingSnooze(undefined);
    if (pending) {
      clearAllSelection();
      snooze(pending.stops, pending.minutes);
    }
  }, [pendingSnooze, snooze, clearAllSelection]);

  const colDefs: ColDef<TManifestStopEnhanced>[] = useMemo(() => {
    return [
      {
        field: "indicatorColor",
        headerName: "",
        pinned: "left",
        lockPosition: "left",
        headerClass: "manifest-detail-header-cell color-header-cell",
        cellClass: "manifest-detail-body-cell color-body-cell",
        lockPinned: true,
        suppressMovable: true,
        resizable: false,
        rowDrag: false,
        width: 20,
        minWidth: 20,
        suppressColumnsToolPanel: true,
        cellRenderer: ColorIndicatorCellRenderer,
        cellRendererParams: {minimal: true},
        // DP-3214: the ordering itself lives in compareIndicatorColumn so both sides of the
        // flag are unit-testable without rendering this screen.
        comparator(valueA, valueB, nodeA, nodeB) {
          return compareIndicatorColumn(
            {
              backendColorRulesEnabled,
              getPriority: (color) => getColorPriority(ruleContextState.rules, color)
            },
            {color: valueA, rank: nodeA?.data?.indicatorRank},
            {color: valueB, rank: nodeB?.data?.indicatorRank}
          );
        }
      },
      {
        colId: "dragColumn",
        headerName: "",
        pinned: "left",
        lockPosition: "left",
        headerClass: "manifest-detail-header-cell drag-header-cell",
        cellClass: "manifest-detail-body-cell drag-body-cell",
        lockPinned: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        sortable: false,
        resizable: false,
        width: 10,
        minWidth: 10,
        rowDrag: (params: RowDragCallbackParams<TManifestStopEnhanced>) =>
          isAllowResequence && !isCompleted(params.data!),
        rowDragText: (params: IRowDragItem) => params.rowNode?.data?.manifestSequence,
        cellRenderer: DragCellRenderer,
        cellRendererParams: (params: ICellRendererParams<TManifestStopEnhanced>) => ({
          isCompleted: isCompleted(params.data!),
          isAllowResequence,
          content: getDragIconTooltipContent({
            isSorting,
            canResequenceByFilter,
            isSelectRowUncontinuously,
            params,
            clearAllSelection
          })
        })
      },
      {
        field: "manifestSequence",
        headerName: "Sequence #",
        cellClass: "manifest-detail-body-cell sequence-body-cell",
        initialWidth: 60,
        lockPosition: "left",
        lockPinned: true,
        suppressMovable: true,
        pinned: "left",
        type: ["filterColumn", "numberFilter"]
      },
      {
        field: "jobStopStatus",
        headerName: "Status",
        cellRenderer: StopStatusRenderer,
        cellStyle: {"--ag-cell-horizontal-padding": "4px", display: "flex", justifyContent: "center"},
        type: ["filterColumn", "setFilter"],
        filterParams: {
          values: Object.values(JobStopStatus),
          valueFormatter: jobStopStatusValueFormatter
        },
        valueFormatter: jobStopStatusValueFormatter,
        initialWidth: 70,
        lockPosition: "left",
        lockPinned: true,
        suppressMovable: true,
        pinned: "left"
      },
      {
        field: "job.jobNumber",
        headerName: "Job #",
        headerClass: "manifest-detail-header-cell manifest-details-job-number-header-cell",
        minWidth: 75,
        headerComponent: JobColHeaderRenderer,
        headerComponentParams: {
          jobColoringIndex: jobColoringIndex,
          stopLength: getArrayFromField(manifestDetailsAssignmentState.selectedManifest?.stops).length
        },
        initialWidth: 100,
        cellRenderer: ColoredJobNumberCellRenderer,
        cellRendererParams: {
          jobColoringIndex: jobColoringIndex,
          usage: JobNumberTooltipUsage.MANIFEST_DETAILS_GRID,
          dispatchNotesEnabled: dispatchNotesEnabled,
          stops: manifestDetailsAssignmentState.selectedManifest?.stops
        },
        type: ["filterColumn", "textFilter"],
        filterValueGetter: (params) => {
          return params.data.job.jobNumber;
        }
      },
      {
        field: "stopType",
        headerName: "Type",
        initialWidth: 70,
        cellRenderer: StopTypeIconCellRenderer,
        type: ["filterColumn", "setFilter"],
        filterParams: {
          values: Object.values(JobStopType),
          valueFormatter: jobStopTypeToString
        },
        filterValueGetter: (params) => params.data.stopType
      },
      {
        field: "scheduledDateTime",
        headerName: "Scheduled Time",
        initialWidth: 120,
        cellRenderer: DateTimeCellRenderer,
        valueGetter: (params) => {
          return getStopDateTimeCellValue({
            data: params.data,
            is24HourFormat: appState.isTimeFormat24hr,
            isTimezoneOfJobStop,
            typeOfDateTime: StopDateTimeType.SCHEDULED_TIME
          });
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const a = getDateTimeByType(nodeA.data, StopDateTimeType.SCHEDULED_TIME)?.getTime() ?? 0;
          const b = getDateTimeByType(nodeB.data, StopDateTimeType.SCHEDULED_TIME)?.getTime() ?? 0;
          return a - b;
        },
        type: ["filterColumn", "dateFilter"],
        filterParams: {
          type: "range",
          isClientSideRangeFilter: true,
          typeOfDateTime: StopDateTimeType.SCHEDULED_TIME
        }
      },
      ...((isETAEnabled && isUseETAService
        ? [
            {
              field: "eta",
              headerName: "ETA",
              headerComponent: ETAColHeaderRenderer,
              headerComponentParams: {
                manifest: {
                  ...manifestDetailsAssignmentState.selectedManifest,
                  etaMessage: manifestETA?.message ?? ""
                }
              },
              initialWidth: 120,
              cellRenderer: ETACellRenderer,
              valueGetter: (params) => {
                return isHideETA(params, etaSourcesDirty)
                  ? undefined
                  : getStopDateTimeCellValue({
                      data: params.data as TManifestStopEnhanced,
                      is24HourFormat: appState.isTimeFormat24hr,
                      isTimezoneOfJobStop,
                      showPreposition: false,
                      typeOfDateTime: StopDateTimeType.ETA
                    });
              },
              filter: false,
              sortable: false
            },
            {
              field: "etaGeneratedDateTime",
              headerName: "ETA Generation Date/Time",
              initialWidth: 200,
              cellRenderer: DateTimeCellRenderer,
              valueGetter: (params) => {
                return isHideETA(params, etaSourcesDirty)
                  ? undefined
                  : getStopDateTimeCellValue({
                      data: params.data as TManifestStopEnhanced,
                      is24HourFormat: appState.isTimeFormat24hr,
                      isTimezoneOfJobStop,
                      showPreposition: false,
                      typeOfDateTime: StopDateTimeType.ETA_GENERATION_TIME
                    });
              },
              filter: false,
              sortable: false
            },
            {
              field: "notificationWindowSent",
              headerName: "ETA Notification Window Sent",
              initialWidth: 220,
              cellRenderer: DateTimeCellRenderer,
              valueGetter: (params) => {
                return getNotificationWindowCellValue(
                  params.data as TManifestStopEnhanced,
                  appState.isTimeFormat24hr,
                  isTimezoneOfJobStop
                );
              },
              valueFormatter: (params) => params.value?.upperField?.value ?? "",
              filter: false,
              sortable: false,
              initialHide: true
            }
          ]
        : []) as ColDef<TManifestStopEnhanced>[]),
      {
        field: "address",
        headerName: "Address",
        cellRenderer: DoubleFieldCellRenderer,
        valueGetter: (params) => {
          return {
            upperField: {
              value: params.data.address,
              tooltip: params.data.address
            },
            lowerField: {
              value: `${params.data.city}, ${params.data.state}, ${params.data.zip}`,
              tooltip: `${params.data.city}, ${params.data.state}, ${params.data.zip}`,
              style: {
                color: "#6B7280"
              }
            }
          } as DoubleFieldCellValue;
        },
        initialWidth: 109,
        comparator(valueA, valueB) {
          const addressA = valueA.upperField.value + valueA.lowerField.value;
          const addressB = valueB.upperField.value + valueB.lowerField.value;
          return addressSort(addressA, addressB);
        },

        type: ["filterColumn", "textFilter"],
        filterValueGetter: (params) => {
          return `${params.data.address} ${params.data.city} ${params.data.state} ${params.data.zip}`;
        },
        cellStyle: {
          cursor: "pointer"
        },
        cellClass: "manifest-detail-address-cell",
        onCellClicked: (params: CellClickedEvent<TManifestStopEnhanced>) => {
          if (!params.data) return;
          const address = [params.data.address, params.data.city, params.data.state, params.data.zip]
            .filter(Boolean)
            .join(", ");
          copyToClipboard(address).then(() => {
            /*Noop*/
          });
        }
      },
      {
        field: "dispatchZone",
        headerName: "Zone",
        cellClass: "manifest-detail-body-cell zone-body-cell",
        initialWidth: 100,
        type: ["filterColumn", "textFilter"]
      },
      {
        field: "order.service",
        headerName: "SVT",
        initialWidth: 100,
        cellStyle: {"--ag-cell-horizontal-padding": "4px"},
        cellRenderer: ServiceTypeCellRenderer,
        type: ["filterColumn"],
        filter: ServiceTypeFilter
      },
      {
        field: "job.pieces",
        headerName: "Pieces",
        initialWidth: 100,
        sortable: true,
        cellRenderer: JobPiecesOrWeightCellRenderer,
        type: ["filterColumn", "numberFilter"]
      },
      {
        field: "job.weight",
        headerName: "Weight",
        initialWidth: 100,
        sortable: true,
        cellRenderer: JobPiecesOrWeightCellRenderer,
        type: ["filterColumn", "numberFilter"]
      },
      {
        field: "name",
        headerName: "Stop Name",
        sortable: true,
        initialWidth: 140,
        cellRenderer: CellRendererWithToolTip,
        type: ["filterColumn", "textFilter"]
      },
      {
        field: "order.customer.name",
        headerName: "Customer Name",
        sortable: true,
        initialWidth: 150,
        tooltipField: "order.customer.name",
        cellRenderer: CellRendererWithToolTip,
        type: ["filterColumn", "textFilter"]
      },
      {
        colId: "details",
        headerName: "Details",
        sortable: false,
        initialWidth: 90,
        cellRenderer: DetailsCellRenderer
      },
      {
        field: "see",
        headerName: "Contact",
        sortable: false,
        initialWidth: 90,
        cellRenderer: ContactCellRenderer,
        valueGetter: ContactValueGetter,
        initialHide: !isTestingEnvironment
      },
      {
        field: "order.notes",
        headerName: "Order Notes",
        sortable: false,
        initialWidth: 90,
        cellRenderer: CellRendererWithToolTip,
        initialHide: !isTestingEnvironment
      },
      {
        field: "note",
        headerName: "Stop Notes",
        sortable: false,
        initialWidth: 90,
        cellRenderer: CellRendererWithToolTip,
        initialHide: !isTestingEnvironment
      },
      {
        field: "order.customer.notes",
        headerName: "Customer Notes",
        sortable: false,
        initialWidth: 90,
        cellRenderer: CellRendererWithToolTip,
        initialHide: !isTestingEnvironment
      },
      {
        field: "order.customer.customerCode",
        headerName: "Account #",
        sortable: true,
        initialWidth: 90,
        cellRenderer: CellRendererWithToolTip,
        type: ["filterColumn", "textFilter"],
        initialHide: !isTestingEnvironment
      },
      {
        field: "order.declaredValue",
        headerName: "Declared Value",
        sortable: true,
        type: ["filterColumn", "numberFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment
      },
      {
        field: "order.codAmount",
        headerName: "COD Amount",
        sortable: true,
        type: ["filterColumn", "numberFilter"],
        initialWidth: 150,
        initialHide: !isTestingEnvironment
      },
      {
        field: "podName",
        headerName: "POD Name",
        sortable: true,
        type: ["filterColumn", "textFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "podDateTime",
        headerName: "POD Time",
        initialWidth: 120,
        cellRenderer: DateTimeCellRenderer,
        valueGetter: (params) => {
          return getStopDateTimeCellValue({
            data: params.data,
            is24HourFormat: appState.isTimeFormat24hr,
            isTimezoneOfJobStop,
            typeOfDateTime: StopDateTimeType.POD_TIME
          });
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const a = getDateTimeByType(nodeA.data, StopDateTimeType.POD_TIME)?.getTime() ?? 0;
          const b = getDateTimeByType(nodeB.data, StopDateTimeType.POD_TIME)?.getTime() ?? 0;
          return a - b;
        },
        type: ["filterColumn", "dateFilter"],
        filterParams: {
          type: "range",
          isClientSideRangeFilter: true,
          typeOfDateTime: StopDateTimeType.POD_TIME
        },
        initialHide: !isTestingEnvironment
      },
      {
        field: "arriveDateTime",
        headerName: "Arrival Time",
        initialWidth: 120,
        cellRenderer: DateTimeCellRenderer,
        valueGetter: (params) => {
          return getStopDateTimeCellValue({
            data: params.data,
            is24HourFormat: appState.isTimeFormat24hr,
            isTimezoneOfJobStop,
            typeOfDateTime: StopDateTimeType.ARRIVAL_TIME
          });
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const a = getDateTimeByType(nodeA.data, StopDateTimeType.ARRIVAL_TIME)?.getTime() ?? 0;
          const b = getDateTimeByType(nodeB.data, StopDateTimeType.ARRIVAL_TIME)?.getTime() ?? 0;
          return a - b;
        },
        type: ["filterColumn", "dateFilter"],
        filterParams: {
          type: "range",
          isClientSideRangeFilter: true,
          typeOfDateTime: StopDateTimeType.ARRIVAL_TIME
        },
        initialHide: !isTestingEnvironment
      },
      {
        field: "departDateTime",
        headerName: "Departure Time",
        initialWidth: 120,
        cellRenderer: DateTimeCellRenderer,
        valueGetter: (params) => {
          return getStopDateTimeCellValue({
            data: params.data,
            is24HourFormat: appState.isTimeFormat24hr,
            isTimezoneOfJobStop,
            typeOfDateTime: StopDateTimeType.DEPARTURE_TIME
          });
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const a = getDateTimeByType(nodeA.data, StopDateTimeType.DEPARTURE_TIME)?.getTime() ?? 0;
          const b = getDateTimeByType(nodeB.data, StopDateTimeType.DEPARTURE_TIME)?.getTime() ?? 0;
          return a - b;
        },
        type: ["filterColumn", "dateFilter"],
        filterParams: {
          type: "range",
          isClientSideRangeFilter: true,
          typeOfDateTime: StopDateTimeType.DEPARTURE_TIME
        },
        initialHide: !isTestingEnvironment
      },
      {
        field: "lateDateTime",
        headerName: "Late Time",
        initialWidth: 120,
        cellRenderer: DateTimeCellRenderer,
        valueGetter: (params) => {
          return getStopDateTimeCellValue({
            data: params.data,
            is24HourFormat: appState.isTimeFormat24hr,
            isTimezoneOfJobStop,
            typeOfDateTime: StopDateTimeType.LATE_TIME
          });
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const a = getDateTimeByType(nodeA.data, StopDateTimeType.LATE_TIME)?.getTime() ?? 0;
          const b = getDateTimeByType(nodeB.data, StopDateTimeType.LATE_TIME)?.getTime() ?? 0;
          return a - b;
        },
        type: ["filterColumn", "dateFilter"],
        filterParams: {
          type: "range",
          isClientSideRangeFilter: true,
          typeOfDateTime: StopDateTimeType.LATE_TIME
        },
        initialHide: !isTestingEnvironment
      },
      {
        field: "earlyDateTime",
        headerName: "Early Time",
        initialWidth: 120,
        cellRenderer: DateTimeCellRenderer,
        valueGetter: (params) => {
          return getStopDateTimeCellValue({
            data: params.data,
            is24HourFormat: appState.isTimeFormat24hr,
            isTimezoneOfJobStop,
            typeOfDateTime: StopDateTimeType.EARLY_TIME
          });
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const a = getDateTimeByType(nodeA.data, StopDateTimeType.EARLY_TIME)?.getTime() ?? 0;
          const b = getDateTimeByType(nodeB.data, StopDateTimeType.EARLY_TIME)?.getTime() ?? 0;
          return a - b;
        },
        type: ["filterColumn", "dateFilter"],
        filterParams: {
          type: "range",
          isClientSideRangeFilter: true,
          typeOfDateTime: StopDateTimeType.EARLY_TIME
        },
        initialHide: !isTestingEnvironment
      },
      {
        colId: "completedTime",
        headerName: "Completed Time",
        initialWidth: 120,
        cellRenderer: DateTimeCellRenderer,
        valueGetter: (params) => {
          return getStopDateTimeCellValue({
            data: params.data,
            is24HourFormat: appState.isTimeFormat24hr,
            isTimezoneOfJobStop,
            typeOfDateTime: StopDateTimeType.COMPLETED_TIME
          });
        },
        comparator(_valueA, _valueB, nodeA, nodeB) {
          const a = getDateTimeByType(nodeA.data, StopDateTimeType.COMPLETED_TIME)?.getTime() ?? 0;
          const b = getDateTimeByType(nodeB.data, StopDateTimeType.COMPLETED_TIME)?.getTime() ?? 0;
          return a - b;
        },
        initialHide: !isTestingEnvironment
      },
      {
        field: "job.routeNumber",
        headerName: "Route Number",
        sortable: true,
        type: ["filterColumn", "textFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "job.site.code",
        headerName: "Site",
        sortable: true,
        type: ["filterColumn", "textFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "order.auth",
        headerName: "Auth",
        sortable: true,
        type: ["filterColumn", "textFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "order.alias",
        headerName: "Order Alias",
        sortable: true,
        type: ["filterColumn", "textFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "job.alias",
        headerName: "Job Alias",
        sortable: true,
        type: ["filterColumn", "textFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "order.caller",
        headerName: "Caller",
        sortable: true,
        type: ["filterColumn", "textFilter"],
        initialWidth: 90,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "order.priority.description",
        headerName: "Order Priority",
        initialHide: !isTestingEnvironment,
        initialWidth: 120,
        type: ["filterColumn", "textFilter"],
        cellRenderer: CellRendererWithToolTip
      },
      {
        field: "order.createDate",
        headerName: "Created Date",
        initialWidth: 120,
        initialHide: !isTestingEnvironment,
        cellRenderer: CellRendererWithToolTip,
        valueGetter: (params) => {
          return DateValueGetter(params, StopDateTimeType.ORDER_CREATED_DATE, isTimezoneOfJobStop);
        },
        comparator(valueA, valueB) {
          return new Date(valueA).getTime() - new Date(valueB).getTime();
        },
        type: ["filterColumn", "dateFilter"],
        filterParams: {
          type: "range",
          isClientSideRangeFilter: true,
          typeOfDateTime: StopDateTimeType.ORDER_CREATED_DATE
        }
      },
      {
        field: "order.user.userName",
        headerName: "User",
        initialWidth: 120,
        initialHide: !isTestingEnvironment,
        type: ["filterColumn", "setFilter"],
        filterParams: {
          values: users
        }
      },
      {
        field: "order.requirements.name",
        headerName: "Order Requirements",
        valueGetter: (params) => params.data.order?.requirements?.map((or: OrderRequirement) => or.name).join(", "),
        initialHide: !isTestingEnvironment,
        sortable: false,
        type: ["filterColumn"],
        filter: "agSetColumnFilter",
        filterParams: {
          values: [AG_GRID_NOT_EXIST_FILTER, ...orderRequirements],
          valueFormatter: (params: any) => {
            if (params.value === AG_GRID_NOT_EXIST_FILTER) {
              return "No Requirements";
            }
            return params.value;
          }
        }
      },
      ...(skillsEnabled
        ? [
            {
              colId: "order.orderSkills.id",
              field: "order.orderSkills.id",
              headerName: "Skills",
              sortable: false,
              initialHide: !isTestingEnvironment,
              type: ["filterColumn", "setFilter"],
              filterParams: getOrderSkillFilterParams(skills),
              filterValueGetter: (params: any) => {
                return params.data.order.orderSkills?.map((skill: Skill) => skill.id);
              },
              valueGetter: (params: any) => {
                return orderSkillsValueGetter(params, skills);
              },
              cellRenderer: CellRendererWithToolTip
            }
          ]
        : [])
    ] as ColDef[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workingStops,
    jobColoringIndex,
    manifestDetailsAssignmentState.selectedManifest,
    dispatchNotesEnabled,
    isETAEnabled,
    isUseETAService,
    isTestingEnvironment,
    skillsEnabled,
    skills,
    ruleContextState.rules,
    backendColorRulesEnabled,
    isAllowResequence,
    isSorting,
    canResequenceByFilter,
    isSelectRowUncontinuously,
    clearAllSelection,
    appState.isTimeFormat24hr,
    isTimezoneOfJobStop,
    etaSourcesDirty,
    DateValueGetter,
    orderRequirements,
    users
  ]);

  const onUpdateManifestVehicle = useCallback(
    (vehicleType: VehicleType) => {
      const prevVehicleTypeId = selectedVehicleTypeId;
      if (vehicleType.vehicleTypeId === selectedVehicleTypeId) {
        return;
      }
      setSelectedVehicleTypeId(vehicleType.vehicleTypeId);
      updateManifestMutation({
        variables: {
          manifestDriverId: manifestDetailsAssignmentState.selectedManifest?.manifestDriverId as number,
          vehicleTypeId: vehicleType.vehicleTypeId
        },
        onCompleted() {
          showToastWithTitle({
            intent: Intent.SUCCESS,
            title: "Vehicle Update Submitted",
            message: "We’ve submitted the request to update this manifest’s vehicle.",
            delay: 3000
          });
        },
        onError() {
          setSelectedVehicleTypeId(prevVehicleTypeId);
          showToastWithTitle({
            intent: Intent.WARNING,
            title: "Vehicle Update Failed",
            message: "Failed to update this manifest’s vehicle. Please try again."
          });
        }
      });
    },
    [manifestDetailsAssignmentState.selectedManifest?.manifestDriverId, selectedVehicleTypeId, updateManifestMutation]
  );

  const onSelectGroupHandler = useCallback(
    (group: TGroupOption) => {
      if (selectedGroup?.key !== group.key) {
        setSelectedGroup(group);
        updateManifestMutation({
          variables: {
            manifestDriverId: manifestDetailsAssignmentState.selectedManifest?.manifestDriverId as number,
            dispatchGroupId: Number(group.key)
          },
          onCompleted() {
            AppToasterTopLeft.show({
              intent: Intent.SUCCESS,
              icon: "tick",
              message:
                "Manifest's driver group updated request has been submitted. Updates will be reflected in the UI after the request has been processed."
            });
          }
        });
      }
      setIsOpenDispatchGroupSelector(false);
    },
    [manifestDetailsAssignmentState.selectedManifest?.manifestDriverId, selectedGroup?.key, updateManifestMutation]
  );

  const onUpdateManifestStatus = useCallback(
    (status: ManifestStatusType) => {
      if (status != selectedStatus) {
        const prevStatus = selectedStatus;
        setSelectedStatus(status);
        updateManifestMutation({
          variables: {
            manifestDriverId: manifestDetailsAssignmentState.selectedManifest?.manifestDriverId as number,
            manifestDriverStatus: status
          },
          onCompleted() {
            showToastWithTitle({
              intent: Intent.SUCCESS,
              title: "Status Update Submitted",
              message: "We’ve submitted the request to update this manifest’s status.",
              delay: 3000
            });
          },
          onError() {
            setSelectedStatus(prevStatus);
            showToastWithTitle({
              intent: Intent.WARNING,
              title: "Status Update Failed",
              message: "Failed to update this manifest’s status. Please try again."
            });
          }
        });
      }
      setIsOpenDispatchGroupSelector(false);
    },
    [manifestDetailsAssignmentState.selectedManifest?.manifestDriverId, selectedStatus, updateManifestMutation]
  );

  const getColorIndicator = useCallback(
    async (stop: TManifestStopEnhanced) => {
      if (
        !ruleContextState.loading &&
        ruleContextState.rules &&
        ruleContextState.rules.length > 0 &&
        manifestDetailsAssignmentState.selectedManifest &&
        stop
      ) {
        const engine = ruleContextState.getConfiguredEngine(
          {manifest: manifestDetailsAssignmentState.selectedManifest, stop: stop},
          ruleContextState.rules.filter((rule) => rule.conditions.name !== ConditionNames.MANIFESTONLY)
        );
        if (engine === undefined) {
          log.warn("Engine not configured");
          return;
        }

        const result = await engine
          .run()
          .then(({events}) => {
            if (events.length > 0) {
              return events[0].params?.color;
            }
          })
          .catch((err) => {
            log.warn("Error getting color indicator", err);
          });
        stop.indicatorColor = result;
      }
    },
    [ruleContextState, manifestDetailsAssignmentState.selectedManifest]
  );

  // DP-3090: the per-stop colour path. decorateStopIndicatorColors is a no-op when backend
  // colour rules are on, so the worker's stamp on each stop survives a row rebuild — see
  // that helper for why overwriting it wiped the colours after every replication. The
  // manifest header path is guarded separately in processHighestRankColor below.
  const buildIndicatorColors = useCallback(
    (newRows: TManifestStopEnhanced[]) =>
      decorateStopIndicatorColors(newRows, getColorIndicator, backendColorRulesEnabled),
    [getColorIndicator, backendColorRulesEnabled]
  );

  const handleDialogVisibility = useCallback((dialogType: DialogType, isVisible: boolean) => {
    switch (dialogType) {
      case DialogType.pendingSequences:
        setHasPendingSeqUpdatesAlertVisible(isVisible);
        break;

      case DialogType.wrongOrder:
        setIsWrongOrderAlertVisible(isVisible);
        if (!isVisible) setWrongOrderJobs([]);
        break;

      case DialogType.normalUnassign:
        setOpenNormalUnassignDialog(isVisible);
        if (!isVisible) setUnassignJobState(undefined);
        break;

      case DialogType.partialUnassign:
        setOpenPartialUnassignDialog(isVisible);
        if (!isVisible) setUnassignJobState(undefined);
        break;

      case DialogType.reassignJob:
        setOpenReassignJobConfirmation(isVisible);
        break;

      case DialogType.exportConfirm:
        setIsOpenExportConfirmDialog(isVisible);
        break;

      case DialogType.routeOptimization:
        setDialogOptimizeRouteOpen(isVisible);
        break;

      default:
        log.warn(`Unknown dialog type: ${dialogType}`);
    }
  }, []);

  const handleCheckoutDriver = () => {
    if (askCheckoutTimeAndMilage && askManifestCheckoutInfoEnabled) {
      closeManifestDialogRef.current?.open();
      return;
    }

    closeManifest({
      variables: {
        manifestDriverId: manifestDetailsAssignmentState.selectedManifest
          ? manifestDetailsAssignmentState.selectedManifest.manifestDriverId
          : 0
      }
    })
      .then((r) => {
        if (r.errors) {
          log.error(r.errors);
          AppToasterTopLeft.show({
            intent: Intent.DANGER,
            icon: "asterisk",
            message: `Unable to close ${manifestDetailsAssignmentState.selectedManifest?.driver.name}'s Manifest`
          });
        } else {
          AppToasterTopLeft.show({
            intent: Intent.SUCCESS,
            icon: "tick",
            message: `Closed ${manifestDetailsAssignmentState.selectedManifest?.driver.name}'s Manifest`
          });
          onClose();
        }
      })
      .catch((reason) => {
        log.error(reason);
        AppToasterTopLeft.show({
          intent: Intent.DANGER,
          icon: "asterisk",
          message: `Unable to close ${manifestDetailsAssignmentState.selectedManifest?.driver.name}'s Manifest`
        });
      });
  };

  const handleSaveResequnceManifest = useCallback(() => {
    const newSequence: JobStopManifestSequence[] = [];
    gridRef.current!.api.forEachNode((row, index) => {
      newSequence.push({
        jobStopId: row.data!.jobStopId,
        manifestSequence: index + 1
      });
    });
    resequenceManifestStops({
      variables: {
        manifestDriverId: manifestDetailsAssignmentState.selectedManifest
          ? manifestDetailsAssignmentState.selectedManifest.manifestDriverId
          : 0,
        jobStopManifestSequences: newSequence,
        isOptimized: isOptimized,
        etaSourcesDirty: etaSourcesDirty,
        optimizationEtas: isOptimized && !isDraggedStops ? optimizationEtas : []
      }
    })
      .then((r) => {
        if (r.errors) {
          log.error(r.errors);
          AppToasterTopLeft.show({
            intent: Intent.DANGER,
            icon: "asterisk",
            message: `Error submitting request to resequence ${manifestDetailsAssignmentState.selectedManifest?.driver.name}'s Manifest`
          });
        } else {
          AppToasterTopLeft.show({
            intent: Intent.SUCCESS,
            icon: "tick",
            message:
              "Manifest resequencing request has been submitted. Updates will be reflected in the UI after the request has been processed."
          });
          setHasPendingSequenceUpdates(false);
          setIsOptimized(false);
          clearAllSelection();
        }
      })
      .catch((reason) => {
        log.error(reason);
        AppToasterTopLeft.show({
          intent: Intent.DANGER,
          icon: "asterisk",
          message: `Error submitting request to resequence ${manifestDetailsAssignmentState.selectedManifest?.driver.name}'s Manifest`
        });
      });
  }, [
    resequenceManifestStops,
    manifestDetailsAssignmentState.selectedManifest,
    isOptimized,
    etaSourcesDirty,
    isDraggedStops,
    optimizationEtas,
    clearAllSelection
  ]);

  const validateManifestStopsWrongOrder = useCallback(() => {
    const wrongOrderJobNumbers = getManifestWrongOrderJobNumbers(workingStops ?? []);
    if (wrongOrderJobNumbers.length > 0) {
      setWrongOrderJobs(wrongOrderJobNumbers);
      handleDialogVisibility(DialogType.wrongOrder, true);
      return false;
    }

    return true;
  }, [handleDialogVisibility, workingStops]);

  const handleCloseManifest = useCallback(() => {
    setIsAutoCloseManifest(false);
    onClose();
  }, [onClose]);

  const onCloseManifestClicked = () => {
    if (hasPendingSequenceUpdates) {
      handleDialogVisibility(DialogType.pendingSequences, true);
    } else {
      handleCloseManifest();
    }
  };

  const onSaveChangesClicked = useCallback(() => {
    if (validateManifestStopsWrongOrder() === false) return;
    handleSaveResequnceManifest();
  }, [handleSaveResequnceManifest, validateManifestStopsWrongOrder]);

  const onSaveAndCloseClicked = () => {
    handleDialogVisibility(DialogType.pendingSequences, false);
    if (validateManifestStopsWrongOrder() === false) {
      setIsAutoCloseManifest(true);
      return;
    }
    handleSaveResequnceManifest();
    handleCloseManifest();
  };

  const onSaveAnywaysClicked = () => {
    handleDialogVisibility(DialogType.wrongOrder, false);
    handleSaveResequnceManifest();
    if (isAutoCloseManifest) handleCloseManifest();
  };

  const onCloseWithoutSavingClicked = () => {
    handleDialogVisibility(DialogType.pendingSequences, false);
    handleCloseManifest();
  };

  const handleRunOptimizeRouteDialog = () => {
    handleDialogVisibility(DialogType.routeOptimization, false);
    handleOptimizeRoute();
  };

  const handleOptimizeDataChange = useCallback((data: OptimizeFormData) => {
    setOptimizeFormData(data);
  }, []);

  const extractOptimizedStops = useCallback(
    (routeOptimizationItem: RouteOptimizationResultItem, optimizedDateTime: Date) => {
      const solution: RouteOptimizationResponse = routeOptimizationItem.solution as RouteOptimizationResponse;
      const activities = solution.solution?.routes?.[0].activities;

      const filteredActivities = uniqBy(
        activities?.filter((act) => !OPTIMIZATION_LOCATION_TYPES.includes(act.type ?? "")),
        (act) => `${act.id}_${act.type}`
      );

      log.debug("MANIFESTDETAILS - activities:", filteredActivities);

      // Extract optimized stops based on activities
      const optimizedStops: TManifestStopEnhanced[] = [];
      filteredActivities.forEach((act: RouteOptimizationActivity) => {
        const jobStopId = extractJobStopIdFromActivity(act);
        const stop = workingStops.find((s) => s.jobStopId === jobStopId);
        if (stop && !optimizedStops.some((x) => x.jobStopId === stop?.jobStopId)) {
          optimizedStops.push(stop);
        } else if (!stop) {
          log.warn(`Stop with jobStopId ${jobStopId} not found in workingStops`);
        }
      });

      // isEtaFromOptimizationEnabled: attach ETAs to optimized stops
      if (isEtaFromOptimizationEnabled) {
        setOptimizationEtas(
          routeOptimizationItem.optimizationEtas?.map((eta) => ({
            eta: eta.eta ?? "",
            etaSource: eta.etaSource ?? "",
            generatedDateTime: eta.generatedDateTime ?? "",
            jobStopId: eta.jobStopId ?? 0
          })) ?? []
        );
        log.debug("MANIFESTDETAILS - Optimized ETAs:", routeOptimizationItem.optimizationEtas ?? []);
      }

      // Insert the completed stops back into their original positions
      let updatedStops = [...optimizedStops];
      const completedStops = workingStops.filter(isCompleted) ?? [];
      for (const completedStop of completedStops) {
        const negativeSequenceCompletedStops = updatedStops.filter((stop) => stop.manifestSequence <= 0);
        updatedStops = [...negativeSequenceCompletedStops, ...updatedStops];
        const originalIndex = completedStop.manifestSequence - 1;
        updatedStops.splice(negativeSequenceCompletedStops.length + originalIndex, 0, completedStop);
      }

      // Re-assign optimizedSequence based on new order to show on map markers
      updatedStops = updatedStops.map((stop, index) => ({
        ...stop,
        optimizedSequence: index + 1
      }));

      return updatedStops;
    },
    [isEtaFromOptimizationEnabled, workingStops]
  );

  const extractOptimizedInfo = useCallback(
    async (routeOptimizationItem: RouteOptimizationResultItem) => {
      const endLocationAddress = routeOptimizationItem.solution?.solution?.routes?.[0].activities?.at(-1)?.address;
      let estimatedCompletionDateTime = "";
      if (endLocationAddress) {
        const endLocationTZ = await getTimeZoneIdByLatLng(endLocationAddress.lat, endLocationAddress.lon);
        const dateStr = routeOptimizationItem.optimizationEtas?.at(-1)?.eta ?? "";
        estimatedCompletionDateTime = isTimezoneOfJobStop
          ? getDateTimeAtSpecificTimeZone({
              dateStr: dateStr,
              isTimeFormat24hr: appState.isTimeFormat24hr,
              timeZone: endLocationTZ
            })
          : getLocalDateTime({dateStr: dateStr, isTimeFormat24hr: appState.isTimeFormat24hr});
      }
      return {
        savedTime: routeOptimizationItem.routeDifference?.timeDifference ?? 0,
        savedDistance: routeOptimizationItem.routeDifference?.distanceDifference ?? 0,
        totalDistance: routeOptimizationItem.solution?.solution?.distance ?? 0,
        estimatedCompletionDateTime: estimatedCompletionDateTime
      };
    },
    [appState.isTimeFormat24hr, isTimezoneOfJobStop]
  );

  const extractOptimizedCords = useCallback(
    (routeOptimizationItem: RouteOptimizationResultItem) => {
      //Original cords does not have end location cords
      const originalCords = decodePolyLine(
        routeOptimizationItem?.originalRoute?.paths?.at(0)?.points?.encodedLine ?? ""
      );
      const optimizePoints = routeOptimizationItem?.solution?.solution?.routes?.at(0)?.points;
      //Remove end locations points from optimized points
      const optimizedCords =
        optimizePoints
          ?.slice(0, optimizeFormData?.endLocation ? -1 : optimizePoints.length)
          .flatMap((item) => item.coordinates)
          .map((cords) => {
            return {lat: cords.at(1) ?? 0, lng: cords.at(0) ?? 0};
          }) ?? [];

      const optimizedResultCordsMap = new Map<OptimizedResultCords, Array<{lat: number; lng: number}>>();
      if (isOptimizeFromHomeLocation) {
        optimizedResultCordsMap.set(OptimizedResultCords.HomeCords, optimizedCords?.slice(0, 1));
      }

      optimizedResultCordsMap.set(OptimizedResultCords.OptimizedCords, optimizedCords);
      optimizedResultCordsMap.set(OptimizedResultCords.OriginalCords, originalCords);

      return optimizedResultCordsMap;
    },
    [isOptimizeFromHomeLocation, optimizeFormData?.endLocation]
  );

  const handleOptimizeRouteErrorResponse = useCallback((optimizationResponseError: any) => {
    log.error("Optimization Error: ", optimizationResponseError);
    if (
      optimizationResponseError?.message.includes("timeout") ||
      optimizationResponseError.message.includes("timed out") ||
      optimizationResponseError.message.includes("InternalError")
    ) {
      setTypeOptimizeErrors("graphhopper_timeout");
    } else {
      setGraphhopperGeneticMessages(optimizationResponseError.message);
      setTypeOptimizeErrors("genetic");
    }
    errorDialogRef.current?.handleOpen();
    optimizeSpinerRef.current?.handleCloseSpinner();
  }, []);

  const handleValidOptimization = useCallback(
    async (routeOptimizationItem: RouteOptimizationResultItem, optimizedDateTime: Date) => {
      const solution: RouteOptimizationResponse = routeOptimizationItem.solution as RouteOptimizationResponse;
      log.debug("MANIFESTDETAILS - response:", solution);

      // Check for unassigned stops error in the solution
      if (solution.solution?.no_unassigned) {
        const unassignedDetailed = solution.solution.unassigned?.details;
        const filterNonTimeWindowErrors = excludeTimeWindowErrors(unassignedDetailed);
        if (filterNonTimeWindowErrors.length > 0) {
          const filterErrorByCode = groupDetailsByCode(filterNonTimeWindowErrors);
          const arrayErrorStops: GraphHopperValidStopMess[][] = [];
          filterErrorByCode.forEach((item) => {
            const errorStops = item.map((x) => {
              const stopByID = manifestDetailsAssignmentState.selectedManifest?.stops?.find((stop) => {
                return stop.jobStopId.toString() === x.id?.split(":")[1];
              });
              const newErrWithAddress = {
                stopId: stopByID?.manifestSequence,
                address: `${stopByID?.address}, ${stopByID?.city}, ${stopByID?.state} ${stopByID?.zip}`
              };
              return {
                ...x,
                ...newErrWithAddress
              };
            });
            arrayErrorStops.push(errorStops as GraphHopperValidStopMess[]);
          });

          setGraphHopperValidStopMessages(arrayErrorStops);
          setTypeOptimizeErrors("graphhopper");
          errorDialogRef.current?.handleOpen();
          optimizeSpinerRef.current?.handleCloseSpinner();
        } else {
          timeWindowErrorDialogRef.current?.handleOpen();
          optimizeSpinerRef.current?.handleCloseSpinner();
        }
        return;
      }

      // Clear any existing sort on the grid for allowing resequence based on optimization
      gridRef.current!.columnApi.applyColumnState({
        defaultState: {sort: null}
      });

      // Extract optimized stops from the optimization result, keep completed stops in their original positions
      const optimizedStops = extractOptimizedStops(routeOptimizationItem, optimizedDateTime);
      log.debug("MANIFESTDETAILS - optimizedStops:", optimizedStops);

      // Update states if sequences have changed
      setIsOptimized(true);
      setHasPendingSequenceUpdates(true);
      setWorkingStops(optimizedStops);
      setEtaSourcesDirty(getEtaSourcesDirty(originalStopsWithETA, gridRef));

      // Update optimized info if route difference is available
      if (routeOptimizationItem.routeDifference) {
        const optimizedInfo = await extractOptimizedInfo(routeOptimizationItem);
        setOptimizedInfo(optimizedInfo);
      }

      // Process optimized coordinates for map display
      const optimizedCords = extractOptimizedCords(routeOptimizationItem);
      setOptimizedResultCords(optimizedCords);

      AppToasterTopLeft.show({
        intent: Intent.SUCCESS,
        icon: "asterisk",
        message: `Optimization Success!   Credits: ${solution?.rateLimitCredits}, proc_time: ${solution?.processing_time}ms, wait_q_time: ${solution?.waiting_time_in_queue}ms, distance: ${solution?.solution?.distance}m, time: ${solution?.solution?.completion_time}s`
      });
      optimizeSpinerRef.current?.handleCloseSpinner();
    },
    [
      extractOptimizedCords,
      extractOptimizedInfo,
      extractOptimizedStops,
      manifestDetailsAssignmentState.selectedManifest?.stops
    ]
  );

  const handleInvalidOptimization = useCallback(
    (routeOptimizationItem: RouteOptimizationResultItem) => {
      const haveServiceId = routeOptimizationItem.validationErrors?.hints?.every((item) => item.serviceId);
      const isStartEndLocationError = routeOptimizationItem.validationErrors?.hints?.some((item) =>
        OPTIMIZATION_LOCATION_TYPES.includes(item.serviceId ?? "")
      );

      const isTimeWindowError = routeOptimizationItem.validationErrors?.hints?.some((item) =>
        item.message.includes("Time windows mismatch:")
      );

      const message = routeOptimizationItem.validationErrors?.message;
      if (!haveServiceId) {
        setGraphhopperGeneticMessages(message ?? "");
        setTypeOptimizeErrors("genetic");
        errorDialogRef.current?.handleOpen();
        optimizeSpinerRef.current?.handleCloseSpinner();
      }

      const notSupportedError = routeOptimizationItem.validationErrors?.hints?.find(
        (hint) => hint.message == "Optimize Manifest Not Supported"
      );

      if (notSupportedError) {
        setGraphhopperGeneticMessages(notSupportedError.details ?? "Something went wrong. Please try again later!");
        setTypeOptimizeErrors("genetic");
        errorDialogRef.current?.handleOpen();
        optimizeSpinerRef.current?.handleCloseSpinner();
      } else {
        if (isStartEndLocationError) {
          const locationRequestInvalidError = routeOptimizationItem.validationErrors?.hints
            ?.filter((item) => OPTIMIZATION_LOCATION_TYPES.includes(item.serviceId ?? ""))
            .map((item) => {
              return {
                message: message ?? "",
                serviceId: item.serviceId ?? "",
                address: extractAddressFromMessage(item.message)
              };
            });
          setGraphHopperInvalidLocationMess(locationRequestInvalidError ?? []);
          setTypeOptimizeErrors("graphhopper");
          errorDialogRef.current?.handleOpen();
          optimizeSpinerRef.current?.handleCloseSpinner();
        } else if (isTimeWindowError) {
          setTypeOptimizeErrors("graphhopper");
          errorDialogRef.current?.handleClose();
          timeWindowErrorDialogRef.current?.handleOpen();
          optimizeSpinerRef.current?.handleCloseSpinner();
        } else {
          const errorValid = routeOptimizationItem.validationErrors?.hints?.map((item) => {
            const stopByID = manifestDetailsAssignmentState.selectedManifest?.stops?.find(
              (h) => h.jobStopId.toString() === item.serviceId
            );
            return {
              address: `${stopByID?.address}, ${stopByID?.city}, ${stopByID?.state} ${stopByID?.zip}`,
              message: message,
              stopId: stopByID?.manifestSequence
            };
          });
          setGraphHopperInvalidStopMessages(errorValid as GraphHopperInvalidStopMess[]);
          setTypeOptimizeErrors("graphhopper");
          errorDialogRef.current?.handleOpen();
          optimizeSpinerRef.current?.handleCloseSpinner();
        }
      }
    },
    [manifestDetailsAssignmentState.selectedManifest?.stops]
  );

  const handleOptimizeRouteSuccessResponse = useCallback(
    (routeOptimizationItem: RouteOptimizationResultItem) => {
      if (routeOptimizationItem.valid) {
        handleValidOptimization(routeOptimizationItem, optimizeDateTimeRef.current ?? new Date());
      } else {
        handleInvalidOptimization(routeOptimizationItem);
      }
    },
    [handleInvalidOptimization, handleValidOptimization]
  );

  const {isBatchOptimizationEnabled, startBatchPolling} = useBatchOptimization(
    {
      routeOptimizationBatchModeStops,
      stopCount: manifestDetailsAssignmentState.selectedManifest?.stopCount ?? 0
    },
    {
      onSuccess: handleOptimizeRouteSuccessResponse,
      onError: handleOptimizeRouteErrorResponse
    }
  );

  const handleOptimizeRoute = async (skipTimeWindowError?: boolean) => {
    optimizeDateTimeRef.current = new Date();
    setInvalidAddresses([]);
    setGraphHopperValidStopMessages([]);
    setGraphHopperInvalidStopMessages([]);
    setGraphhopperGeneticMessages("");
    setGraphHopperInvalidLocationMess([]);
    setNewStatusJobs([]);
    setOptimizedInfo(undefined);
    setOptimizedResultCords(null);
    setIsDraggedStops(false);
    optimizeSpinerRef?.current?.handleOpenSpinner();
    const {startingLocation, endLocation} = optimizeFormData ?? {};
    try {
      // DP-3154 — a stop in New status cannot be optimized, and the optimizer's own error for
      // it reads "stop #undefined" (DP-3148). Checked here, ahead of the address validation
      // below, because that one awaits geocoding and this manifest is going to fail anyway.
      //
      // Outside the skipTimeWindowError guard on purpose: "optimize anyway, ignoring time
      // windows" must not skip it, since ignoring time windows does nothing about an
      // unsupported stop status.
      //
      // Reads state rather than the grid's rows — the Status filter's values are fixed to
      // {A, C, Q}, so a New stop is hidden even under Select All.
      const jobsWithNewStatusStops = findJobsWithNewStatusStops(manifestDetailsAssignmentState.selectedManifest?.stops);
      if (jobsWithNewStatusStops.length > 0) {
        setNewStatusJobs(jobsWithNewStatusStops);
        setTypeOptimizeErrors("new_stop_status");
        errorDialogRef.current?.handleOpen();
        optimizeSpinerRef.current?.handleCloseSpinner();
        return;
      }

      if (!skipTimeWindowError) {
        const invalidStartEndLocations: OptimizationLocation[] =
          (await filterInvalidStartEndLocation(startingLocation!, endLocation)) ?? [];
        const invalidStopAddresses: OptimizationLocation[] =
          (await filterInvalidAddresses(
            findNotCompletedStops(manifestDetailsAssignmentState.selectedManifest?.stops)
          )) ?? [];
        if (
          (invalidStopAddresses && invalidStopAddresses.length > 0) ||
          (invalidStartEndLocations && invalidStartEndLocations.length > 0)
        ) {
          setTypeOptimizeErrors("googleGeo");
          setInvalidAddresses([...invalidStartEndLocations, ...invalidStopAddresses]);
          errorDialogRef?.current?.handleOpen();
          optimizeSpinerRef.current?.handleCloseSpinner();
          return;
        }
      }

      if (manifestDetailsAssignmentState.selectedManifest) {
        const startingDateTime = Date.parse(optimizeFormData?.startingTime as string);
        const request: RouteOptimizationRequest = {
          manifestDriverId: manifestDetailsAssignmentState.selectedManifest.manifestDriverId,
          dwellTime: optimizeFormData?.dwellTime as number,
          startingDateTime: isNaN(startingDateTime)
            ? ""
            : new Date(new Date(startingDateTime).setSeconds(0, 0)).toISOString(),
          startingLocation,
          endLocation,
          source: Constants.GRAPHHOPPER_SOURCE,
          // DP-3187: both undefined unless the dialog showed and filled "First Stop After Pickup"
          startingJobStopId: optimizeFormData?.startingJobStopId,
          firstStopAfterPickupJobStopId: optimizeFormData?.firstStopAfterPickupJobStopId
        };

        // Build optimization request options
        const routeOptimizationRequestOptions = [
          {
            requestOption: RouteOptimizationRequestOptionType.IgnoreTimeWindows,
            enabled: skipTimeWindowError ?? !!optimizeFormData?.ignoreTimeWindows
          },
          {
            requestOption: RouteOptimizationRequestOptionType.IncludeOriginalRouteDetail,
            enabled: true
          },
          {
            requestOption: RouteOptimizationRequestOptionType.EncodeOriginalRoutePoints,
            enabled: true
          }
        ];

        if (isBatchOptimizationEnabled) {
          routeOptimizationRequestOptions.push({
            requestOption: RouteOptimizationRequestOptionType.BatchMode,
            enabled: true
          });
        }

        const optimizationResponse = await optimizeRoutes({
          variables: {
            routeOptimizationRequests: [request],
            routeOptimizationRequestOptions
          }
        });

        if (optimizationResponse.error) {
          handleOptimizeRouteErrorResponse(optimizationResponse.error);
          return;
        }

        const routeOptimizationItem: RouteOptimizationResultItem = optimizationResponse.data?.optimizeRoutes
          ?.items[0] as RouteOptimizationResultItem;

        if (routeOptimizationItem) {
          // Check if this is a batch mode response (has jobId but no full solution)
          const solution = routeOptimizationItem.solution;
          const ghJobId = solution?.job_id;
          const hasSolutionFromCache = !!solution?.cached && !!solution?.solution;
          const shouldPollForBatchResult = isBatchOptimizationEnabled && !!ghJobId && !hasSolutionFromCache;

          if (shouldPollForBatchResult) {
            log.info(`Starting batch optimization polling for jobId: ${ghJobId}`);
            startBatchPolling(ghJobId);
            return;
          }

          // Normal mode: full solution returned. For batch mode, full solution can be returned from cache, in which case we process immediately without polling.
          handleOptimizeRouteSuccessResponse(routeOptimizationItem);
        }
      }
    } catch (error) {
      log.error("Optimization Error: ", error);
      setTypeOptimizeErrors("genetic");
      if (errorDialogRef?.current) {
        errorDialogRef.current.handleOpen();
      }
    }
  };

  const handleOptimizeIgnoreTimeWindow = () => {
    handleOptimizeRoute(true);
  };

  const handleUnassignJobConfirm = () => {
    if (manifestDetailsAssignmentState.selectedManifest && unassignJobState?.jobStopIds) {
      unassignJob({
        variables: {
          manifestDriverId: manifestDetailsAssignmentState.manifestId!,
          jobStopIds: unassignJobState?.jobStopIds
        }
      })
        .then((r) => {
          if (r.errors) {
            log.error(r.errors);
            AppToasterTopLeft.show({
              intent: Intent.DANGER,
              icon: "asterisk",
              message: `Unable to unassign ${renderUnassignJobIdsText} from ${manifestDetailsAssignmentState.selectedManifest?.driver.name}`
            });
          } else {
            AppToasterTopLeft.show({
              intent: Intent.SUCCESS,
              icon: "tick",
              message: `Unassigned ${renderUnassignJobIdsText} from ${manifestDetailsAssignmentState.selectedManifest?.driver.name}`
            });
          }
        })
        .catch((reason) => {
          log.error(reason);
          AppToasterTopLeft.show({
            intent: Intent.DANGER,
            icon: "asterisk",
            message: `Unable to unassign ${renderUnassignJobIdsText} from ${manifestDetailsAssignmentState.selectedManifest?.driver.name}`
          });
        })
        .finally(() => {
          handleDialogVisibility(DialogType.partialUnassign, false);
          handleDialogVisibility(DialogType.normalUnassign, false);
          clearAllSelection();
        });
    }
  };

  const handleUnassignJobConfirmWithCheck = () => {
    handleDialogVisibility(DialogType.normalUnassign, false);
    handleDialogVisibility(DialogType.partialUnassign, false);

    const stops = manifestDetailsAssignmentState.selectedManifest?.stops ?? [];
    checkEverCompletedBeforeAction(unassignJobState?.jobIds ?? [], stops, "unassigned", () =>
      handleUnassignJobConfirm()
    );
  };

  const handleMarkJobStopCompleteConfirm = () => {
    if (manifestDetailsAssignmentState.selectedManifest) {
      const manifestStop = (manifestDetailsAssignmentState.selectedManifest.stops ?? []).find(
        (s) => s.jobStopId === markCompletedJobStopId
      );
      if (!manifestStop) return;
      handleUpdateJobStopCall({
        manifest: manifestDetailsAssignmentState.selectedManifest,
        updateJobStopVariables: [
          {jobStopId: markCompletedJobStopId!, jobStopStatus: manifestStop.jobStopStatus, nextJobStopStatus: "Y"}
        ],
        jobStopId: markCompletedJobStopId!,
        callType: UpdateStopCallType.COMPLETE,
        finallyCallback: () => setMarkCompletedJobStopId(undefined)
      });
    }
  };

  const handleMarkJobStopIncompleteConfirm = () => {
    if (manifestDetailsAssignmentState.selectedManifest) {
      handleUpdateJobStopCall({
        manifest: manifestDetailsAssignmentState.selectedManifest,
        updateJobStopVariables: [
          {
            jobStopId: markIncompleteJobStopId!,
            jobStopStatus: "A",
            nextJobStopStatus: "N",
            podName: " ",
            arriveDateTimeSource: SaveAndClearJobStopDateTimeSource.NOT_SET,
            departDateTimeSource: SaveAndClearJobStopDateTimeSource.NOT_SET
          }
        ],
        jobStopId: markIncompleteJobStopId!,
        callType: UpdateStopCallType.INCOMPLETE,
        finallyCallback: () => setMarkIncompleteJobStopId(undefined)
      });
    }
  };

  const handleAdjustJobsStopTimesConfirm = (adjustTimeState: AdjustTimeState) => {
    if (manifestDetailsAssignmentState.selectedManifest && adjustStops && adjustStops?.length > 0) {
      const {success, failed} = buildAdjustToastMessages(adjustStops);
      // DP-3051: one saveClearJobStops call per job (smallest jobStopId only); the backend
      // cascades to the job's other stops. All-or-nothing toast across the parallel calls.
      const calls = buildAdjustStopCalls(adjustStops, adjustTimeState);
      log.debug("MANIFESTDETAILS - Adjust time:", calls);
      Promise.all(
        calls.map((saveClearJobStops) =>
          updateJobStop({variables: {saveClearJobStops}}).then((r) => {
            if (r.errors) {
              throw r.errors;
            }
            return r;
          })
        )
      )
        .then(() => {
          AppToasterTopLeft.show({intent: Intent.SUCCESS, icon: "tick", message: success});
        })
        .catch((reason) => {
          log.error(reason);
          AppToasterTopLeft.show({intent: Intent.DANGER, icon: "asterisk", message: failed});
        })
        .finally(() => setAdjustStops(undefined));
    }
  };

  const handleUpdateJobStopCall = ({
    manifest,
    updateJobStopVariables,
    jobStopId,
    callType,
    finallyCallback
  }: HandleUpdateStopCallProps) => {
    const successMessage = `Mark ${callType} ${jobStopId} from ${manifest.driver.name}`;
    const failedMessage = `Unable to Mark ${callType} ${jobStopId} from ${manifest.driver.name}`;

    updateJobStop({
      variables: {
        saveClearJobStops: updateJobStopVariables
      }
    })
      .then((r: any) => {
        if (r.errors) {
          log.error(r.errors);
          AppToasterTopLeft.show({
            intent: Intent.DANGER,
            icon: "asterisk",
            message: failedMessage
          });
        } else {
          AppToasterTopLeft.show({
            intent: Intent.SUCCESS,
            icon: "tick",
            message: successMessage
          });
        }
      })
      .catch((reason: any) => {
        log.error(reason);
        AppToasterTopLeft.show({
          intent: Intent.DANGER,
          icon: "asterisk",
          message: failedMessage
        });
      })
      .finally(finallyCallback);
  };

  const defaultColDef: ColDef = useMemo<ColDef>(() => {
    return {
      resizable: true,
      suppressMenu: true,
      autoHeight: true,
      sortable: true,
      headerClass: "manifest-detail-header-cell",
      cellClass: "manifest-detail-body-cell",
      minWidth: 45
    };
  }, []);

  const manifestHasOpenStops = hasOpenStops(manifestDetailsAssignmentState.selectedManifest?.stops);

  const exceedMaximumManifest = hasTooManyStops(manifestDetailsAssignmentState.selectedManifest);

  const decorateDispatchNotes = useCallback(
    async (stops: TManifestStopEnhanced[]) => {
      if (dispatchNotesEnabled) {
        const orderIds = Array.from(new Set(stops.map((s) => s.order?.orderId).filter(Boolean) as number[]));
        if (orderIds.length > 0) {
          const notesMap = await fetchDispatchNotes(apolloClient, orderIds);
          stops.forEach((stop) => {
            const orderId = stop.order?.orderId;
            if (orderId != null && notesMap.has(orderId)) {
              stop.order = {...stop.order!, orderEvents: notesMap.get(orderId)!};
            }
          });
        }
      }
    },
    [apolloClient, dispatchNotesEnabled]
  );

  const processStopColors = useCallback(() => {
    const currentSequences: JobStopManifestSequence[] = [];
    const currentOrderEventsMap = new Map<number, TManifestStopEnhanced["order"]>();
    gridRef?.current?.api.forEachNode((row, index) => {
      currentSequences.push({
        jobStopId: row.data!.jobStopId,
        manifestSequence: index + 1
      });
      if (row.data?.order?.orderEvents) {
        currentOrderEventsMap.set(row.data.jobStopId, row.data.order);
      }
    });
    if (currentSequences.length > 0 && manifestDetailsAssignmentState.selectedManifest) {
      const newStops = addOptimizedSequenceToStops(currentSequences, manifestDetailsAssignmentState.selectedManifest);
      // Preserve dispatch notes (orderEvents) that were decorated onto working stops
      newStops.forEach((stop) => {
        const existingOrder = currentOrderEventsMap.get(stop.jobStopId);
        if (existingOrder?.orderEvents && stop.order) {
          stop.order = {...stop.order, orderEvents: existingOrder.orderEvents};
        }
      });
      applyETAToStops(newStops);
      // DP-3176: when using client-side color rules, defer colour processing
      // until the initial ETA query has resolved so the engine evaluates with
      // ETA data from the start — avoids a visible colour flicker (no-ETA
      // colours → ETA colours) on panel open.
      if (!backendColorRulesEnabled && etaLoading) {
        setWorkingStops(newStops);
        return;
      }
      buildIndicatorColors(newStops).then(() => {
        setWorkingStops(newStops);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buildIndicatorColors,
    manifestDetailsAssignmentState.selectedManifest?.stops,
    backendColorRulesEnabled,
    etaLoading
  ]);

  const processDispatchNotes = useCallback(async () => {
    if (!dispatchNotesEnabled || !gridRef.current?.api) return;
    const stops: TManifestStopEnhanced[] = [];
    gridRef.current.api.forEachNode((row) => {
      if (row.data) stops.push(row.data);
    });
    if (stops.length === 0) return;
    const orderIds = Array.from(new Set(stops.map((s) => s.order?.orderId).filter(Boolean) as number[]));
    if (orderIds.length === 0) return;
    const notesMap = await fetchDispatchNotes(apolloClient, orderIds);
    let hasChanges = false;
    stops.forEach((stop) => {
      const orderId = stop.order?.orderId;
      if (orderId != null && notesMap.has(orderId)) {
        const freshEvents = notesMap.get(orderId)!;
        const currentEvents = stop.order?.orderEvents;
        if (JSON.stringify(currentEvents) !== JSON.stringify(freshEvents)) {
          stop.order = {...stop.order!, orderEvents: freshEvents};
          hasChanges = true;
        }
      }
    });
    if (hasChanges) {
      setWorkingStops([...stops]);
    }
  }, [apolloClient, dispatchNotesEnabled]);

  const processHighestRankColor = useCallback(async () => {
    if (ruleContextState.loading || !manifestDetailsAssignmentState.selectedManifest || !ruleContextState.colorRank)
      return;
    // DP-3049: when backend color rules are enabled, the worker has already
    // stamped indicatorColor onto each stop. Read those directly instead of
    // re-running the client-side rules engine, mirroring ManifestDriverList so
    // the manifest header reflects the highest-ranked stop color.
    if (backendColorRulesEnabled) {
      const run = ++highestRankColorRunRef.current;
      // DP-3070: a stop snoozed from this drawer no longer counts towards the manifest colour.
      const manifestColor: StopColor[] = applySnoozedStops(
        manifestDetailsAssignmentState.selectedManifest.stops ?? [],
        snoozedStops,
        Date.now()
      )
        .filter((stop) => Boolean(stop.indicatorColor))
        .map((stop) => ({color: stop.indicatorColor as string, jobStopID: stop.jobStopId}));

      // DP-3292: a manifest colour rule stamps no stop — the worker has nowhere to put a
      // manifest-level colour — so its colour is evaluated here and merged in before the
      // header picks a winner. Merged AFTER applySnoozedStops on purpose: it belongs to no
      // stop, so no snooze can apply to it.
      const manifestOnlyColor = await getManifestOnlyColor({
        manifest: manifestDetailsAssignmentState.selectedManifest,
        ruleContextState
      });
      // Superseded by a newer run, or the drawer closed while this one awaited.
      if (run !== highestRankColorRunRef.current) return;
      manifestOnlyColor && manifestColor.push(manifestOnlyColor);

      setHighestRankIndicatorColor(getHighestRankedColor(manifestColor, ruleContextState.colorRank ?? []));
      return;
    }
    // The client branch below has the same await-then-setState shape via getManifestColors,
    // and the same exposure — but it predates DP-3292 and decision D5 keeps the flag-off path
    // out of scope, so it is deliberately left as it was rather than changed here.
    //
    // DP-3176: defer client-side header colour until initial ETAs have loaded,
    // matching the guard in processStopColors to prevent flicker.
    if (etaLoading) return;
    // DP-3176: apply ETAs to a copy of the stops before running the engine,
    // so ETA-based rules can read the eta field.
    const stopsWithETA = (manifestDetailsAssignmentState.selectedManifest.stops ?? []).map((s) => ({...s}));
    applyETAToStops(stopsWithETA);
    const manifestWithETA = {...manifestDetailsAssignmentState.selectedManifest, stops: stopsWithETA};
    const manifestColor = await getManifestColors({
      manifest: manifestWithETA,
      ruleContextState
    });
    setHighestRankIndicatorColor(getHighestRankedColor(manifestColor, ruleContextState.colorRank ?? []));
  }, [
    manifestDetailsAssignmentState.selectedManifest,
    ruleContextState,
    backendColorRulesEnabled,
    etaLoading,
    snoozedStops
  ]);

  const checkForSequenceChanges = useCallback(() => {
    if (!gridRef.current?.api) {
      setHasPendingSequenceUpdates(false);
      return;
    }
    processStopColors();
    processHighestRankColor();
    setHasPendingSequenceUpdates(
      isOptimized || isStopsSequenceChanged(manifestDetailsAssignmentState.selectedManifest?.stops, gridRef)
    );
    setEtaSourcesDirty(getEtaSourcesDirty(originalStopsWithETA, gridRef));
  }, [processStopColors, processHighestRankColor, isOptimized, manifestDetailsAssignmentState.selectedManifest?.stops]);

  const onRowSelected = useMemo(
    () =>
      debounce((e: RowSelectedEvent<TManifestStopEnhanced>) => {
        if (["apiSelectAll", "api"].includes(e?.source)) return;
        let newSelectedStops: TManifestStopEnhanced[] = [];
        const isReassigning = manifestDetailsAssignmentState.detailsViewType === "ASSIGNDRIVER";
        const isSplitJob = manifestDetailsAssignmentState.detailsViewType === "SPLITJOB";
        if (isReassigning) {
          const filteredCompletedJobNumbers = new Set<string>();
          const filteredAlreadySplitJobNumbers = new Set<string>();
          e.api.getSelectedNodes().forEach((node) => {
            const data = node.data;
            if (!data) return;

            const correspondedJobStops = getArrayFromField(
              manifestDetailsAssignmentState.selectedManifest?.stops?.filter((x) => x.job.jobId === data.job.jobId)
            );
            const isAllCompleted = correspondedJobStops.every(isCompleted);
            const isAlreadySplit = isJobAlreadySplit(correspondedJobStops);

            if (isAllCompleted || isAlreadySplit) {
              node.setSelected(false);
              if (isAllCompleted) {
                filteredCompletedJobNumbers.add(data.job.jobNumber ?? EmptyValueStrings.unknown);
              }
              if (isAlreadySplit) {
                filteredAlreadySplitJobNumbers.add(data.job.jobNumber ?? EmptyValueStrings.unknown);
              }
            }
          });
          newSelectedStops = e.api.getSelectedNodes().map((node) => node.data) as TManifestStopEnhanced[];
          if (filteredCompletedJobNumbers.size > 0) {
            AppToaster.show({
              intent: Intent.WARNING,
              message: `The following jobs were not selected for reassignment because all their stops are already completed: ${Array.from(
                filteredCompletedJobNumbers
              ).join(", ")}`,
              className: "toast",
              timeout: 3000
            });
          }
          if (filteredAlreadySplitJobNumbers.size > 0) {
            AppToaster.show({
              intent: Intent.WARNING,
              message: `The following jobs were not selected for reassignment because they have already been split: ${Array.from(
                filteredAlreadySplitJobNumbers
              ).join(", ")}`,
              className: "toast",
              timeout: 3000
            });
          }
        } else if (isSplitJob) {
          const selectedNodes = e.api.getSelectedNodes();
          if (selectedNodes.length > 0) {
            const firstData = selectedNodes[0].data;
            if (firstData) {
              const referenceJobId = selectedStops[0]?.job.jobId ?? firstData.job.jobId;
              const allJobStops = getArrayFromField(
                manifestDetailsAssignmentState.selectedManifest?.stops?.filter((ms) => ms.job.jobId === referenceJobId)
              );

              const isAllCompleted = allJobStops.every(isCompleted);
              const isAllIncomplete = allJobStops.every((stop) => !isCompleted(stop));

              if (isAllCompleted || isAllIncomplete) {
                selectedNodes.forEach((node) => {
                  if (node.data?.job.jobId === referenceJobId) {
                    node.setSelected(false);
                  }
                });

                AppToaster.show({
                  intent: Intent.WARNING,
                  message: isAllCompleted
                    ? `This job is already fully completed and cannot be split: ${firstData.job.jobNumber}`
                    : `This job is entirely incomplete and cannot be split: ${firstData.job.jobNumber}. Split Job is only for partially completed jobs.`,
                  className: "toast",
                  timeout: 3000
                });
              } else {
                const invalidJobNumbers = new Set<string>();
                const completedJobNumbers = new Set<string>();
                const alreadySplitJobNumbers = new Set<string>();

                selectedNodes.forEach((node) => {
                  const data = node.data;
                  if (!data) return;

                  const isDifferentJob = data.job.jobId !== referenceJobId;
                  const isStopCompleted = isCompleted(data);
                  const isStopAlreadySplit = !!data.job.driverId2;

                  if (isDifferentJob || isStopCompleted || isStopAlreadySplit) {
                    node.setSelected(false);
                    if (isDifferentJob) {
                      invalidJobNumbers.add(data.job.jobNumber ?? EmptyValueStrings.unknown);
                    }
                    if (isStopCompleted) {
                      completedJobNumbers.add(data.job.jobNumber ?? EmptyValueStrings.unknown);
                    }
                    if (isStopAlreadySplit) {
                      alreadySplitJobNumbers.add(data.job.jobNumber ?? EmptyValueStrings.unknown);
                    }
                  }
                });

                if (invalidJobNumbers.size > 0) {
                  AppToaster.show({
                    intent: Intent.WARNING,
                    message: `Only stops from the same job can be selected during a split. The following jobs were deselected: ${Array.from(
                      invalidJobNumbers
                    ).join(", ")}`,
                    className: "toast",
                    timeout: 3000
                  });
                }

                if (completedJobNumbers.size > 0) {
                  AppToaster.show({
                    intent: Intent.WARNING,
                    message: `Completed stops cannot be selected for splitting: ${Array.from(completedJobNumbers).join(
                      ", "
                    )}`,
                    className: "toast",
                    timeout: 3000
                  });
                }

                if (alreadySplitJobNumbers.size > 0) {
                  AppToaster.show({
                    intent: Intent.WARNING,
                    message: `Only stops from a job that has not been split yet can be selected during a split. The following jobs were deselected: ${Array.from(
                      alreadySplitJobNumbers
                    ).join(", ")}`,
                    className: "toast",
                    timeout: 3000
                  });
                }
              }
            }
          }
          newSelectedStops = e.api.getSelectedNodes().map((node) => node.data) as TManifestStopEnhanced[];
        } else {
          newSelectedStops = e.api.getSelectedNodes().map((node) => node.data) as TManifestStopEnhanced[];
        }
        assignmentDispatch({type: "SetSelectedStops", payload: newSelectedStops});
      }, 50),
    [
      assignmentDispatch,
      manifestDetailsAssignmentState.detailsViewType,
      manifestDetailsAssignmentState.selectedManifest?.stops,
      selectedStops
    ]
  );

  const resetManifestStops = useCallback(() => {
    // DP-3200: a manifest over the tenant's stop cap comes back with stops: null — the replication
    // Lambda omits them and explains itself in metadata.alerts. Guarding on `stops` returned before
    // setWorkingStops, so the previous manifest's rows stayed on screen, the grid was never empty,
    // and ag-Grid never rendered ManifestDetailsNoRowsOverlay. Guard on the manifest itself; the
    // path below already collapses to an empty grid. The loading flags stay non-clearing — they
    // mean "not ready yet", not "no stops".
    if (!manifestDetailsAssignmentState.selectedManifest || ruleContextState.loading || etaLoading) return;

    const initialStops: TManifestStopEnhanced[] = getArrayFromField(
      manifestDetailsAssignmentState.selectedManifest?.stops
    );
    const newStops = addOptimizedSequenceToStops(initialStops, manifestDetailsAssignmentState.selectedManifest);
    applyETAToStops(newStops);
    buildIndicatorColors(newStops).then(async () => {
      await decorateDispatchNotes(newStops);
      setWorkingStops(newStops);
      setHasPendingSequenceUpdates(false);
      setIsOptimized(false);
      setIsDraggedStops(false);
      setEtaSourcesDirty([]);
    });
  }, [
    buildIndicatorColors,
    decorateDispatchNotes,
    manifestDetailsAssignmentState.selectedManifest,
    ruleContextState.loading,
    etaLoading
  ]);

  const formattedManifestDate = () => {
    return getFormattedManifestDate(
      manifestDetailsAssignmentState.selectedManifest?.manifestDate as string,
      DateFormats.longDate
    );
  };

  const handleExportCSV = useCallback(
    (skip = false) => {
      if (!skip && !isEmpty(gridRef.current!.api.getFilterModel())) {
        handleDialogVisibility(DialogType.exportConfirm, true);
        return;
      }

      const prependContent: CsvCell[][] | string = [
        [{data: {value: "Driver Name: " + manifestDetailsAssignmentState.selectedManifest?.driver.name}}],
        [{data: {value: "Driver Code: " + manifestDetailsAssignmentState.selectedManifest?.driver.code}}],
        [
          {
            data: {
              value: "Manifest Number: " + manifestDetailsAssignmentState.selectedManifest?.manifestDriverId.toString()
            }
          }
        ]
      ];

      const fileName = `${format(new Date(), DateFormats.monthDay)} - ${
        manifestDetailsAssignmentState.selectedManifest?.driver.name
      }`.replace(Constants.REGEX.INVALID_FILE_NAME_REGEX, "_");

      const exportParams: CsvExportParams = {
        fileName,
        columnKeys: gridRef
          .current!.columnApi.getAllDisplayedColumns()
          .map((col) => col.getColId())
          .filter((colId) => !UnExportedColumnIds.includes(colId)),
        prependContent: prependContent,
        processCellCallback(cellParams) {
          if (cellParams.column.getColId() === "address") {
            const addressValue = cellParams.value as DoubleFieldCellValue;
            if (!addressValue) return "";
            return [addressValue.upperField.value, addressValue.lowerField.value].join(", ");
          }
          if (cellParams.column.getColId() === "notificationWindowSent") {
            // Export leads with the date (day first), unlike the time-first on-screen cell.
            return getNotificationWindowExportValue(
              cellParams.node?.data as TManifestStopEnhanced,
              appState.isTimeFormat24hr,
              isTimezoneOfJobStop
            );
          }
          if (
            [
              "scheduledDateTime",
              "eta",
              "etaGeneratedDateTime",
              "lateDateTime",
              "earlyDateTime",
              "podDateTime",
              "arriveDateTime",
              "departDateTime",
              "completedTime"
            ].includes(cellParams.column.getColId())
          ) {
            const stopTimeValue = cellParams.value;
            if (!stopTimeValue) return "";
            return [stopTimeValue.lowerField.value, stopTimeValue.upperField.value].join(" ");
          }
          return cellParams.value;
        }
      };

      gridRef.current!.api.exportDataAsCsv(exportParams);
      handleDialogVisibility(DialogType.exportConfirm, false);
    },
    [
      manifestDetailsAssignmentState.selectedManifest?.driver.code,
      manifestDetailsAssignmentState.selectedManifest?.driver.name,
      manifestDetailsAssignmentState.selectedManifest?.manifestDriverId,
      handleDialogVisibility,
      appState.isTimeFormat24hr,
      isTimezoneOfJobStop
    ]
  );

  const hasFiltersOtherThanStopStatus = (filterModel: Record<string, unknown>): boolean =>
    Object.keys(filterModel).some((key) => key !== "jobStopStatus");

  const onFilterChange = (event: FilterChangedEvent) => {
    if (gridReady && !initializing) {
      clearAllSelection();
      const filterState = extractJsonPref(userPreferences, UserPreferences.manifestDetailGridFilterState)?.value;
      const newFilterState = event.api.getFilterModel();
      if (!isEqual(filterState, newFilterState))
        setUserPref({
          variables: {
            name: UserPreferences.manifestDetailGridFilterState,
            input: createJsonPref(newFilterState, true)
          }
        }).then(() => {
          if (userPrefsQueryRefetch) userPrefsQueryRefetch();
        });

      if (Object.keys(newFilterState).length === 0) {
        setCanResequenceByFilter(true);
      } else if (Object.keys(newFilterState).length >= 2) {
        setCanResequenceByFilter(false);
      } else {
        setCanResequenceByFilter(
          newFilterState.jobStopStatus &&
            newFilterState.jobStopStatus.values.includes(JobStopStatus.ACTIVE) &&
            newFilterState.jobStopStatus.values.includes(JobStopStatus.QUEUED)
        );
      }
    }
  };

  const updateColumn = useCallback(
    (columnApi: ColumnApi) => {
      const columnState = extractJsonPref(userPreferences, UserPreferences.manifestDetailGridColumnState)?.value;
      const newColumnState = columnApi.getColumnState();
      if (gridReady && !isEqual(columnState, newColumnState) && !initializing) {
        setUserPref({
          variables: {
            name: UserPreferences.manifestDetailGridColumnState,
            input: createJsonPref(newColumnState, true)
          }
        }).then(() => {
          if (userPrefsQueryRefetch) userPrefsQueryRefetch();
        });
      }
    },
    [initializing, userPreferences, gridReady, setUserPref, userPrefsQueryRefetch]
  );

  const onColumnSortChanged = useCallback(
    (e: SortChangedEvent<TManifestStopEnhanced>) => {
      const isSorting = Boolean(e.columnApi.getColumnState().find((s) => s.sort != null));
      setIsSorting(isSorting);
      updateColumn(e.columnApi);
    },
    [updateColumn]
  );

  const onColumnMoveChanged = useCallback(
    (e: ColumnMovedEvent<TManifestStopEnhanced>) => {
      if (!e.finished || e.source === "api") return;
      updateColumn(e.columnApi);
    },
    [updateColumn]
  );

  const onColumnVisibleChanged = useCallback(
    (e: ColumnVisibleEvent<TManifestStopEnhanced>) => {
      updateColumn(e.columnApi);
    },
    [updateColumn]
  );

  const onFirstDataRendered = (event: FirstDataRenderedEvent) => {
    const filterState = extractJsonPref(userPreferences, UserPreferences.manifestDetailGridFilterState);
    if (!filterState.value) {
      // Use setFilterModel API with a timeout to ensure the filter is fully initialized
      setTimeout(() => {
        const filterModel = {
          jobStopStatus: {
            filterType: "set",
            values: [JobStopStatus.ACTIVE.toString(), JobStopStatus.QUEUED.toString()]
          }
        };
        event.api.setFilterModel(filterModel);
      }, 100);
    }

    setIsFirstDataRendered(true);
  };

  const onDragging = (event: RowDragMoveEvent) => {
    setIsOutsideAgGrid(false);
    checkRowMoveAndDoFunction(event, handleScrollTop);
    return getDraggingStopsList(event, getArrayFromField(workingStops), setWorkingStops);
  };

  const onDragEnd = () => {
    checkForSequenceChanges();
    setIsDraggedStops(true);
  };

  const onRowDragLeave = useCallback((event: RowDragLeaveEvent) => {
    setIsOutsideAgGrid(true);
    const totalRows = gridRef.current?.api.getModel()?.getRowCount();
    checkRowMoveAndDoFunction(event, handleScrollTop, totalRows);
  }, []);

  const onRowDragEnter = useCallback(() => {
    setRowDataWhenStartedDragging(workingStops);
  }, [workingStops]);

  const handleGridReady = useCallback(() => {
    setGridReady(true);
  }, []);

  const proceedWithUnassignDialog = useCallback(
    (unassignState: UnassignJobState) => {
      if (!canUnassignPartialJob) {
        handleDialogVisibility(DialogType.normalUnassign, true);
      } else {
        const filterStopsByJobID = manifestDetailsAssignmentState.selectedManifest?.stops?.filter((ms) =>
          unassignState.jobIds?.includes(ms.job.jobId)
        );
        if (
          filterStopsByJobID?.find((ms) => isCompleted(ms)) &&
          filterStopsByJobID?.find((ms) => isActiveOrQueued(ms))
        ) {
          handleDialogVisibility(DialogType.partialUnassign, true);
        } else {
          handleDialogVisibility(DialogType.normalUnassign, true);
        }
      }
    },
    [canUnassignPartialJob, manifestDetailsAssignmentState.selectedManifest?.stops, handleDialogVisibility]
  );

  const checkEverCompletedBeforeAction = useCallback(
    (jobIds: number[], stops: TManifestStopEnhanced[], actionText: string, onPass: () => void) => {
      const uniqueJobIds = [...new Set(jobIds)];
      const jobInfoMap = new Map(
        uniqueJobIds.map((jobId) => {
          const stop = stops.find((s) => s.job.jobId === jobId);
          return [jobId, {jobNumber: stop?.job.jobNumber ?? String(jobId), orderId: stop?.order?.orderId}];
        })
      );

      setCurrentActionText(actionText);

      getEverCompletedJobs(uniqueJobIds, jobInfoMap)
        .then((result) => {
          if (!result || result.length === 0) {
            onPass();
          } else {
            setEverCompletedJobs(result);
            setShowEverCompletedDialog(true);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .catch(() => {});
    },
    [getEverCompletedJobs]
  );

  const handleUnassignJobClicked = useCallback(
    (unassignJobState: UnassignJobState) => {
      setUnassignJobState(unassignJobState);

      const stops = manifestDetailsAssignmentState.selectedManifest?.stops ?? [];
      checkEverCompletedBeforeAction(unassignJobState.jobIds, stops, "unassigned", () =>
        proceedWithUnassignDialog(unassignJobState)
      );
    },
    [proceedWithUnassignDialog, manifestDetailsAssignmentState.selectedManifest?.stops, checkEverCompletedBeforeAction]
  );

  const proceedWithReassign = useCallback(
    (singleSelectedJobStop?: TManifestStopEnhanced) => {
      manifestDetailsAssignmentDispatch({type: "SET_DETAILS_VIEW_TYPE", payload: "ASSIGNDRIVER"});

      if (dashboardFilters && manifestDetailsAssignmentState.assignDriverManifestFilterState) {
        manifestDetailsAssignmentDispatch({
          type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
          payload: dashboardFilters.copy()
        });
      }

      const newSelectedStops: TManifestStopEnhanced[] = [];
      const selectedJobIds = singleSelectedJobStop
        ? [singleSelectedJobStop.job.jobId]
        : [...new Set(selectedStops.map((js) => js.job.jobId))];

      gridRef.current?.api.forEachNode((node) => {
        if (selectedJobIds.includes(node.data?.job.jobId ?? 0)) {
          const isCompletedStop = isCompleted(node.data!);
          node.setSelected(!isCompletedStop);
          if (!isCompletedStop) {
            newSelectedStops.push(node.data!);
          }
        }
      });

      assignmentDispatch({type: "SetSelectedStops", payload: newSelectedStops});
    },
    [
      manifestDetailsAssignmentDispatch,
      assignmentDispatch,
      selectedStops,
      dashboardFilters,
      manifestDetailsAssignmentState.assignDriverManifestFilterState
    ]
  );

  const handleReassignJobClicked = useCallback(
    (singleSelectedJobStop?: TManifestStopEnhanced) => {
      const selectedJobIds = singleSelectedJobStop
        ? [singleSelectedJobStop.job.jobId]
        : [...new Set(selectedStops.map((js) => js.job.jobId))];
      const stops = manifestDetailsAssignmentState.selectedManifest?.stops ?? [];

      checkEverCompletedBeforeAction(selectedJobIds, stops, "reassigned", () =>
        proceedWithReassign(singleSelectedJobStop)
      );
    },
    [
      proceedWithReassign,
      selectedStops,
      manifestDetailsAssignmentState.selectedManifest?.stops,
      checkEverCompletedBeforeAction
    ]
  );

  const handleSplitJob = useCallback(
    (jobId: number) => {
      manifestDetailsAssignmentDispatch({type: "SET_DETAILS_VIEW_TYPE", payload: "SPLITJOB"});

      // Synchronize Sidebar filters with the latest dashboard filters upon entering Split Job
      if (dashboardFilters && manifestDetailsAssignmentState.assignDriverManifestFilterState) {
        manifestDetailsAssignmentDispatch({
          type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
          payload: dashboardFilters.copy()
        });
      }

      const newSelectedStops: TManifestStopEnhanced[] = [];
      gridRef.current?.api.forEachNode((node) => {
        if (node.data?.job.jobId === jobId) {
          if (isCompleted(node.data)) {
            node.setSelected(false);
          } else {
            newSelectedStops.push(node.data as TManifestStopEnhanced);
            node.setSelected(true);
          }
        }
      });
      assignmentDispatch({type: "SetSelectedStops", payload: newSelectedStops});
    },
    [
      assignmentDispatch,
      manifestDetailsAssignmentDispatch,
      manifestDetailsAssignmentState.assignDriverManifestFilterState,
      dashboardFilters
    ]
  );

  const proceedWithReassignToManifest = useCallback(() => {
    const isMatchRequirementsManifest =
      getMatchRequirementManifests({
        selectedRows: selectedStops,
        manifests: [selectedManifest as Manifest],
        isSkillsBasedRequirements: isSkillsBased,
        activeSkills: skills
      }).length === 1;

    const isRequirementsEmpty =
      selectedStops.filter((stop) => (isSkillsBased ? stop?.order?.orderSkills : stop?.order?.requirements)).length ===
      0;

    if (
      !driverRequirementsEnabled ||
      driverRequiredSetting === DriverRequirementEnforcementLevel.Allow ||
      isRequirementsEmpty ||
      isMatchRequirementsManifest
    ) {
      setOpenReassignJobConfirmation(true);
    } else {
      setShowWarningReassignJob(true);
    }
  }, [driverRequiredSetting, isSkillsBased, selectedManifest, selectedStops, skills, driverRequirementsEnabled]);

  const handleReassignJobToManifest = useCallback(() => {
    const jobIds = selectedStops.map((s) => s.job.jobId);
    checkEverCompletedBeforeAction(jobIds, selectedStops, "reassigned", () => proceedWithReassignToManifest());
  }, [proceedWithReassignToManifest, selectedStops, checkEverCompletedBeforeAction]);

  const handleReassignJobConfirm = useCallback(
    (reasons?: Record<number, string>) => {
      assignmentDispatch({type: "SetOriginManifest", payload: manifestDetailsAssignmentState.selectedManifest});
      manifestDetailsAssignmentDispatch({type: "SET_DETAILS_VIEW_TYPE", payload: "MAP"});
      handleDialogVisibility(DialogType.reassignJob, false);
      setShowWarningReassignJob(false);

      const activeJobStopIds =
        manifestDetailsAssignmentState.selectedManifest?.stops
          ?.filter((stop) => !isCompleted(stop))
          .map((stop) => stop.jobStopId) ?? [];
      const reassignAbleJobStopIds = selectedJobStopIds.filter((id) => activeJobStopIds.includes(id));

      const skillOverrideNotes =
        reasons && Object.keys(reasons).length > 0
          ? buildSkillOverrideNotesFromStops({
              stops: selectedStops,
              reasons,
              driverSkills: selectedManifest?.driver?.skills,
              activeSkills: skills
            })
          : [];

      assignmentDispatch({
        type: "ReassignJobStops",
        payload: {
          reassignFunction: reassignJobStops,
          jobStopIds: reassignAbleJobStopIds,
          keepManifestSequence: reassignAbleJobStopIds.length === activeJobStopIds.length,
          authState: authState,
          authToken: oktaAuth.getAccessToken() ?? "",
          fromManifestDriverId: manifestDetailsAssignmentState.manifestId!,
          manifestDetailsDispatch: manifestDetailsDispatch,
          ...(skillOverrideNotes.length ? {skillOverrideNotes} : {})
        }
      });
      setEnableQualified(false);
      clearAllSelection();
    },
    [
      assignmentDispatch,
      manifestDetailsAssignmentState.selectedManifest,
      manifestDetailsAssignmentState.manifestId,
      manifestDetailsAssignmentDispatch,
      handleDialogVisibility,
      selectedJobStopIds,
      reassignJobStops,
      authState,
      oktaAuth,
      manifestDetailsDispatch,
      clearAllSelection,
      selectedStops,
      selectedManifest,
      skills
    ]
  );

  const handleSplitJobAssignClicked = useCallback(() => {
    (async () => {
      optimizeSpinerRef?.current?.handleOpenSpinner();
      try {
        const token = oktaAuth.getAccessToken();
        if (token) {
          const response = await getSiteConfig(token, splitJobDriver?.siteId ?? 0, "splitpercentage");
          const configValue = response?.data?.configValue;
          if (configValue) {
            const parsedValue = parseFloat(configValue);
            if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 100) {
              setDriverSplitPercentage(configValue);
            } else {
              setDriverSplitPercentage(undefined);
            }
          }
        }
      } catch (error) {
        log.error("Error fetching site config:", error);
        setDriverSplitPercentage(undefined);
      } finally {
        optimizeSpinerRef?.current?.handleCloseSpinner();
        setOpenSplitJobConfirmation(true);
      }
    })();
  }, [oktaAuth, setDriverSplitPercentage, splitJobDriver?.siteId]);

  const handleSplitJobConfirm = useCallback(() => {
    splitJobMutation({
      variables: {
        driverId: splitJobDriver?.driverId ?? 0,
        jobId: selectedStops[0]?.job?.jobId ?? 0,
        splitPercentage: driverSplitPercentage ?? ""
      }
    })
      .then(() => {
        showSplitJobToastSuccess(
          selectedStops[0]?.job?.jobNumber ?? "",
          splitJobDriver?.name ?? "",
          selectedStops[0]?.order?.orderId ?? "",
          authState,
          oktaAuth.getAccessToken() ?? ""
        );
        setOpenSplitJobConfirmation(false);
        setDriverSplitPercentage(undefined);
        manifestDetailsAssignmentDispatch({type: "SET_DETAILS_VIEW_TYPE", payload: "MAP"});
      })
      .catch((error) => {
        log.error("Error splitting job:", error);
        showSplitJobErrorToast(
          selectedStops[0]?.job?.jobNumber ?? "",
          manifestDetailsAssignmentState.selectedManifest?.driver.name ?? "",
          splitJobDriver?.name ?? "",
          selectedStops[0]?.order?.orderId ?? "",
          authState,
          oktaAuth.getAccessToken() ?? ""
        );
      });
  }, [
    splitJobMutation,
    splitJobDriver,
    selectedStops,
    driverSplitPercentage,
    setOpenSplitJobConfirmation,
    setDriverSplitPercentage,
    manifestDetailsAssignmentDispatch,
    manifestDetailsAssignmentState,
    authState,
    oktaAuth
  ]);

  const handleSplitJobCancel = useCallback(() => {
    setOpenSplitJobConfirmation(false);
    setOpenCancelSplitJobConfirmation(true);
  }, []);

  const handleReturnToSplitJobPayment = useCallback(() => {
    setOpenCancelSplitJobConfirmation(false);
    setOpenSplitJobConfirmation(true);
  }, []);

  const handleFinalSplitJobCancel = useCallback(() => {
    setOpenSplitJobConfirmation(false);
    setOpenCancelSplitJobConfirmation(false);
    setDriverSplitPercentage(undefined);
  }, []);

  const onCancelReassignment = useCallback(() => {
    manifestDetailsAssignmentDispatch({type: "SET_DETAILS_VIEW_TYPE", payload: "MAP"});
    assignmentDispatch({type: "SetSplitJobDriver", payload: undefined});
    setEnableQualified(false);
    clearAllSelection();
  }, [clearAllSelection, manifestDetailsAssignmentDispatch, assignmentDispatch]);

  const handleToggleQualified = useCallback(() => {
    setEnableQualified((prev) => !prev);
  }, []);

  const completeStopDialogManifestStop = useMemo(() => {
    return manifestDetailsAssignmentState.selectedManifest?.stops?.find(
      (s) => s.jobStopId === markCompletedJobStopId
    ) as TManifestStopEnhanced;
  }, [manifestDetailsAssignmentState.selectedManifest?.stops, markCompletedJobStopId]);

  const completeStopDialogComplete = useCallback(
    (success: boolean) => {
      if (success) {
        AppToasterTopLeft.show({
          intent: Intent.SUCCESS,
          icon: "tick",
          message: `Mark ${UpdateStopCallType.COMPLETE} ${markCompletedJobStopId} from ${manifestDetailsAssignmentState.selectedManifest?.driver.name}`
        });
      } else {
        AppToasterTopLeft.show({
          intent: Intent.DANGER,
          icon: "asterisk",
          message: `Unable to Mark ${UpdateStopCallType.COMPLETE} ${markCompletedJobStopId} from ${manifestDetailsAssignmentState.selectedManifest?.driver.name}`
        });
      }
      setMarkCompletedJobStopId(undefined);
    },
    [manifestDetailsAssignmentState.selectedManifest?.driver.name, markCompletedJobStopId]
  );

  const completeStopDialogCancel = useCallback(() => {
    setMarkCompletedJobStopId(undefined);
  }, []);

  const isDispatchGroupChanged = useMemo(() => {
    if (!selectedGroup) return false;

    if (driverInfo) {
      return selectedGroup.key !== mappedDriverInfo?.dispatchGroupId;
    }

    if (manifestDetailsAssignmentState.selectedManifest?.dispatchGroup) {
      return (
        manifestDetailsAssignmentState.selectedManifest?.dispatchGroup?.dispatchGroupId !==
        mappedDriverInfo?.dispatchGroupId
      );
    }
    return false;
  }, [
    selectedGroup,
    driverInfo,
    mappedDriverInfo?.dispatchGroupId,
    manifestDetailsAssignmentState.selectedManifest?.dispatchGroup
  ]);

  const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressColumnSelectAll: true,
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true
          }
        }
      ],
      defaultToolPanel: "customColumns"
    };
  }, []);

  const openOrCloseToolPanel = (key: string) => {
    if (gridReady) {
      if (gridRef.current!.api.getOpenedToolPanel() === key) {
        gridRef.current!.api.closeToolPanel();
      } else {
        gridRef.current!.api.openToolPanel(key);
      }
    }
  };

  // Load user preferences for column state and filter state
  useEffect(() => {
    if (gridReady && isUserPreferencesLoaded && initializing && isFirstDataRendered) {
      const columnState = extractJsonPref(userPreferences, UserPreferences.manifestDetailGridColumnState);
      if (columnState.value) {
        ["indicatorColor", "dragColumn"].forEach((colId) => {
          const existColIndex = columnState.value?.findIndex((item: any) => item.colId === colId);
          if (existColIndex > -1) {
            columnState.value[existColIndex].width = colDefs.find((col) => col.colId === colId)?.width;
          }
        });

        backfillNewColumns(columnState.value);
        gridRef.current!.columnApi.applyColumnState({state: columnState.value, applyOrder: true});
      }
      const filterState = extractJsonPref(userPreferences, UserPreferences.manifestDetailGridFilterState);
      if (Object.keys(filterState.value ?? {}).length !== 0) {
        gridRef.current!.api.setFilterModel(filterState.value);
        if (hasFiltersOtherThanStopStatus(filterState.value ?? {})) {
          activeFilterDialogRef.current?.onOpen();
        }
      }
      setInitializing(false);
    }
  }, [gridReady, userPreferences, initializing, colDefs, isFirstDataRendered, isUserPreferencesLoaded]);

  // Reset manifest stops when manifest or rules context changes
  useEffect(() => {
    resetManifestStops();
  }, [resetManifestStops]);

  // Refetch ETA immediately when an update event arrives
  const lastETARefetch = useRef(0);
  useEffect(() => {
    if (
      dataChangedState?.updated &&
      dataChangedState.entityId === manifestDriverId.toString() &&
      dataChangedState.updated > lastETARefetch.current
    ) {
      lastETARefetch.current = dataChangedState.updated + 500;
      ETArefetch();
    }
  }, [dataChangedState, manifestDriverId, ETArefetch]);

  // DP-3176: re-run processStopColors when new ETAs arrive so colors
  // and header update immediately instead of waiting for the 15s poll.
  useEffect(() => {
    if (!manifestETA) return;
    processStopColors();
    processHighestRankColor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestETA]);

  useEffect(() => {
    if (!gridRef.current?.api) {
      setHasPendingSequenceUpdates(false);
      return;
    }
    let cancelled = false;
    const processGridUpdates = () => {
      if (cancelled) return;
      processStopColors();
      processHighestRankColor();
      processDispatchNotes();
    };
    processGridUpdates();
    const interval = setInterval(processGridUpdates, Constants.GENERAL_POLLING_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [processHighestRankColor, processStopColors, processDispatchNotes]);

  // Ensure manifest is visible on map when selected
  useEffect(() => {
    if (manifestDetailsAssignmentState.selectedManifest && mapVisibilityState.manifests.size === 0) {
      mapVisibilityDispatch({
        type: "IncrementManifestVisibility",
        manifest: manifestDetailsAssignmentState.selectedManifest
      });
    }
  }, [manifestDetailsAssignmentState.selectedManifest, mapVisibilityDispatch, mapVisibilityState.manifests.size]);

  // Update selected status and vehicle type when manifest changes
  useEffect(() => {
    setSelectedStatus(manifestDetailsAssignmentState.selectedManifest?.manifestStatus);
    setSelectedVehicleTypeId(manifestDetailsAssignmentState.selectedManifest?.vehicleType.vehicleTypeId);
  }, [
    manifestDetailsAssignmentState.selectedManifest?.manifestStatus,
    manifestDetailsAssignmentState.selectedManifest?.vehicleType.vehicleTypeId
  ]);

  // Handle mouse up event outside of Ag-Grid during drag-and-drop
  useEffect(() => {
    const handleMouseUpOutsideAgGrid = () => {
      handleScrollTop("stop");
      setIsOutsideAgGrid(false);
      setWorkingStops(rowDataWhenStartedDragging);
    };
    if (isOutsideAgGrid) {
      document.addEventListener("mouseup", handleMouseUpOutsideAgGrid);
    } else {
      document.removeEventListener("mouseup", handleMouseUpOutsideAgGrid);
    }
    return () => {
      document.removeEventListener("mouseup", handleMouseUpOutsideAgGrid);
    };
  }, [isOutsideAgGrid, rowDataWhenStartedDragging]);

  // Update job requirements based on selected stops and driver requirements settings
  useEffect(() => {
    if (!driverRequirementsEnabled) return;
    const debouncedDispatch = debounce(() => {
      if (!isEnableQualified) {
        assignmentDispatch({type: "SetReassignJobsRequirements", payload: new Map<number, JobRequirement>()});
      } else {
        const driverRequirementsIds = new Map<number, JobRequirement>();
        selectedStops.forEach((stop) => {
          if (isSkillsBased) {
            const activeSkillIds = skills.map((skill) => skill.id);
            stop.order?.orderSkills?.forEach((skill) => {
              if (activeSkillIds.includes(skill.id)) {
                driverRequirementsIds.set(skill.id, {id: skill.id, name: skill.name} as JobRequirement);
              }
            });
          } else {
            stop.order?.requirements?.forEach((requirement) => {
              driverRequirementsIds.set(requirement.id, {id: requirement.id, name: requirement.name} as JobRequirement);
            });
          }
        });
        assignmentDispatch({
          type: "SetReassignJobsRequirements",
          payload: driverRequirementsIds
        });
      }
    }, 500);

    debouncedDispatch();

    return () => {
      debouncedDispatch.cancel();
    };
  }, [assignmentDispatch, selectedStops, isEnableQualified, driverRequirementsEnabled, skills, isSkillsBased]);

  return (
    <ManifestDriverDetailsWrapper>
      <ManifestDriverDetailsContainer data-testid={"manifest-details-container"}>
        <ActiveFilterDialog
          ref={activeFilterDialogRef}
          title="Active Filters Present"
          message="To view all stops on the manifest, please clear any active filters in the tablet that may be limiting your stop list"
          icon={<Icon icon="filter" color="green" size={21} />}
          actionButton={{
            label: "Remove Filter(s)",
            action: () => gridRef.current?.api.setFilterModel(null)
          }}
        />
        <AssignJobsConfirmation
          testIdPrefix={"manifest-details-reassign-job"}
          isVisible={showReassignJobConfirmation}
          assignmentState={useAssignmentState()}
          driverRequiredSetting={driverRequiredSetting}
          handleConfirm={handleReassignJobConfirm}
          handleClose={() => setShowWarningReassignJob(false)}
          isSkillsBasedRequirements={isSkillsBased}
        />
        <EverCompletedCheckDialog
          testIdPrefix="manifest-details-ever-completed"
          isVisible={showEverCompletedDialog}
          everCompletedJobs={everCompletedJobs}
          actionText={currentActionText}
          handleClose={() => {
            setShowEverCompletedDialog(false);
            setEverCompletedJobs([]);
          }}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-unassign-job"}
          isVisible={openNormalUnassignDialog}
          header={"Unassign Job?"}
          body={`Are you sure you want to unassign the Job ${renderUnassignJobIdsText} from ${manifestDetailsAssignmentState.selectedManifest?.driver.name}?`}
          yesText={"Yes, Unassign"}
          handleYesClicked={handleUnassignJobConfirmWithCheck}
          handleNoClicked={() => handleDialogVisibility(DialogType.normalUnassign, false)}
          handleClose={() => handleDialogVisibility(DialogType.normalUnassign, false)}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-unassign-partial-job"}
          isVisible={openPartialUnassignDialog}
          header={"Unassign Partly Complete Job?"}
          body={"Are you sure you want to unassign this partially complete job?"}
          yesText={"Yes"}
          noText={"Cancel"}
          handleYesClicked={handleUnassignJobConfirmWithCheck}
          handleNoClicked={() => handleDialogVisibility(DialogType.partialUnassign, false)}
          handleClose={() => handleDialogVisibility(DialogType.partialUnassign, false)}
        />
        {markCompletedJobStopId !== undefined && completeStopDialogManifestStop && (
          <CompleteStopDialog
            manifest={manifestDetailsAssignmentState.selectedManifest as TManifestEnhanced}
            manifestStop={completeStopDialogManifestStop}
            markComplete={completeStopDialogComplete}
            onCancel={completeStopDialogCancel}
          />
        )}
        <ConfirmationDialog
          testIdPrefix={"manifest-details-mark-jobstop-incomplete"}
          isVisible={markIncompleteJobStopId !== undefined}
          header={"Mark Incomplete?"}
          body={"Are you sure you want to mark the job stop incomplete?"}
          yesText={"Yes, Mark incomplete"}
          handleYesClicked={handleMarkJobStopIncompleteConfirm}
          handleNoClicked={() => setMarkIncompleteJobStopId(undefined)}
          handleClose={() => setMarkIncompleteJobStopId(undefined)}
        />
        <SnoozeConfirmationDialog
          pending={pendingSnooze}
          onConfirm={handleSnoozeConfirmed}
          onCancel={handleSnoozeCancelled}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-pending-seq-updates-alert"}
          isVisible={hasPendingSeqUpdatesAlertVisible}
          header={"Close Manifest?"}
          body={"You have unsaved adjustments that will be lost if you close the manifest."}
          yesText={"Save and Close"}
          noText={"Close Without Saving"}
          yesTextColor={"#32A467"}
          handleYesClicked={onSaveAndCloseClicked}
          handleNoClicked={onCloseWithoutSavingClicked}
          handleClose={() => handleDialogVisibility(DialogType.pendingSequences, false)}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-wrong-order-alert"}
          isVisible={isWrongOrderAlertVisible}
          header={"Save Changes?"}
          body={`There are stops from ${wrongOrderJobs.join(
            ", "
          )} that seem to be in the wrong order. Pickups must be before deliveries.`}
          yesText={"Save Anyways"}
          noText={"Cancel"}
          handleYesClicked={onSaveAnywaysClicked}
          handleNoClicked={() => handleDialogVisibility(DialogType.wrongOrder, false)}
          handleClose={() => handleDialogVisibility(DialogType.wrongOrder, false)}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-reassign-job"}
          isVisible={openReassignJobConfirmation}
          header={"Reassign a Job?"}
          body={`Are you sure you want to reassign the Job ${renderReassignJobText} from Driver  ${manifestDetailsAssignmentState.selectedManifest?.driver.name} to Driver ${selectedManifest?.driver.name}?`}
          yesText={"Yes, Reassign"}
          handleYesClicked={handleReassignJobConfirm}
          handleNoClicked={() => handleDialogVisibility(DialogType.reassignJob, false)}
          handleClose={() => handleDialogVisibility(DialogType.reassignJob, false)}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-split-job-confirm"}
          isVisible={openSplitJobConfirmation}
          width={390}
          yesDisabled={!driverSplitPercentage || driverSplitPercentage === "."}
          icon={<DollarIcon />}
          header={"Adjust Drivers’ Payments"}
          body={
            <AdjustPaymentBody
              splitJobDriverName={splitJobDriver?.name ?? EmptyValueStrings.unknown}
              originalDriverName={
                manifestDetailsAssignmentState.selectedManifest?.driver.name ?? EmptyValueStrings.unknown
              }
              driverSplitPercentage={driverSplitPercentage}
              setDriverSplitPercentage={setDriverSplitPercentage}
            />
          }
          yesText={"Save and Split"}
          noText={"Cancel"}
          yesTextColor={"#32A467"}
          handleYesClicked={handleSplitJobConfirm}
          handleNoClicked={handleSplitJobCancel}
          handleClose={handleSplitJobCancel}
          canOutsideClickClose={false}
          canEscapeKeyClose={false}
          yesLoading={splitJobLoading}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-split-job-cancel-confirm"}
          isVisible={openCancelSplitJobConfirmation}
          header={"Cancel Split Job?"}
          body={
            <span>
              Canceling splitting will keep all the stops of the Job{" "}
              {JobLinksRenderer([
                {
                  jobNumber: selectedStops[0]?.job?.jobNumber,
                  jobId: selectedStops[0]?.job?.jobId,
                  orderId: selectedStops[0]?.order?.orderId
                }
              ])}{" "}
              in original driver’s manifest.
            </span>
          }
          yesText={"Yes, Cancel Split Job"}
          noText={"Cancel"}
          handleYesClicked={handleFinalSplitJobCancel}
          handleNoClicked={handleReturnToSplitJobPayment}
          handleClose={handleReturnToSplitJobPayment}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-adjust-time-job"}
          isVisible={adjustStops !== undefined}
          icon={<></>}
          header={"Adjust Stop Time"}
          body={
            <AdjustStopTime
              stops={adjustStops!}
              onConfirm={handleAdjustJobsStopTimesConfirm}
              onClose={() => setAdjustStops(undefined)}
            />
          }
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          handleYesClicked={() => {}}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          handleNoClicked={() => {}}
          handleClose={() => setAdjustStops(undefined)}
          useFooter={false}
        />
        <ConfirmationDialog
          testIdPrefix={"manifest-details-export"}
          isVisible={isOpenExportConfirmDialog}
          header={"Export with Filtered Data?"}
          body={
            "The export will contain what’s currently shown within the filters. Remove filters if you’d like all information in the export"
          }
          yesText={"Continue"}
          noText={"Cancel"}
          handleYesClicked={() => handleExportCSV(true)}
          handleNoClicked={() => handleDialogVisibility(DialogType.exportConfirm, false)}
          handleClose={() => handleDialogVisibility(DialogType.exportConfirm, false)}
        />
        {/* Toolbar buttons, window operator buttons */}
        <ButtonsHeaderContainer>
          <ToolbarButtonsContainer>
            <CloseManifestToolTip
              position={Position.RIGHT}
              disabled={!manifestHasOpenStops}
              intent={Intent.WARNING}
              content={"All stops must be complete before the driver is Checked Out."}
            >
              <ManifestToolbarActionButton
                disabled={manifestHasOpenStops || exceedMaximumManifest}
                onClick={handleCheckoutDriver}
              >
                Check Out Driver
              </ManifestToolbarActionButton>
            </CloseManifestToolTip>
            {manifestOptimizationFeature && (
              <>
                <ManifestToolbarActionButton
                  disabled={exceedMaximumManifest}
                  onClick={() => handleDialogVisibility(DialogType.routeOptimization, true)}
                >
                  Optimize Manifest
                </ManifestToolbarActionButton>
                <OptimizedStatus
                  optimizedDateTime={manifestDetailsAssignmentState.selectedManifest?.optimizedDateTime}
                  optimizedBy={manifestDetailsAssignmentState.selectedManifest?.optimizedBy ?? ""}
                />
                <ETAServiceStatus />
              </>
            )}
          </ToolbarButtonsContainer>
          <WindowButtonsContainer>
            <ManifestToolbarButton
              variant="minimal"
              disabled={!gridReady}
              onClick={() => handleExportCSV(false)}
              icon={<Icon icon="import" size={18} />}
              title="Export CSV"
              data-testid={"manifest-details-export-btn"}
            />
            <ManifestToolbarButton
              variant="minimal"
              data-testid={"manifest-details-close-btn"}
              onClick={onCloseManifestClicked}
              icon={<Icon icon="cross" size={24} />}
            />
          </WindowButtonsContainer>
        </ButtonsHeaderContainer>
        {dialogOptimizeRouteOpen && (
          <OptimizeDialog
            onClose={() => handleDialogVisibility(DialogType.routeOptimization, false)}
            onSave={handleRunOptimizeRouteDialog}
            onDataChange={handleOptimizeDataChange}
            manifest={manifestDetailsAssignmentState.selectedManifest}
            homeAddress={manifestDetailsAssignmentState.manifestDriverHomeAddress}
            setIsOptimizeFromHomeLocation={setIsOptimizeFromHomeLocation}
            driverLocationResult={driverLocationResult}
          />
        )}
        <CloseManifestDialog
          ref={closeManifestDialogRef}
          manifestDriverId={manifestDriverId}
          manifestDriverName={manifestDetailsAssignmentState.selectedManifest?.driver.name ?? "driver"}
          closeManifestFunction={onClose}
          ampm={!appState.isTimeFormat24hr}
          manifestEvents={manifestDetailsAssignmentState.selectedManifest?.events}
          initStartMileage={manifestDetailsAssignmentState.selectedManifest?.startMileage}
          initEndMileage={manifestDetailsAssignmentState.selectedManifest?.endMileage}
        />
        <OptimizeSpiner ref={optimizeSpinerRef} size={SIZE_OF_OPTIMIZED_SPINNER} />
        <ErrorDialog
          ref={errorDialogRef}
          typeError={typeOptimizeErrors}
          invalidAddresses={invalidAddresses}
          graphHopperInvalidStopMess={graphHopperInvalidStopMessages}
          graphHopperValidStopMess={graphHopperValidStopMessages}
          graphHopperGeneticMess={graphHopperGeneticMess}
          graphHopperInvalidLocationMess={graphHopperInvalidLocationMess}
          newStatusJobs={newStatusJobs}
        />
        <TimeWindowErrorDialog
          isIgnoreTimeWindow={
            extractSimplePref(tenantPreferences, TenantPreferences.dispatcherIgnoreTimeWindows, false).value as boolean
          }
          ref={timeWindowErrorDialogRef}
          handleIgnoreTimeWindow={handleOptimizeIgnoreTimeWindow}
        />
        <DriverCode data-testid={"driver-code"}>
          <Text className="driver-code-text" data-testid={"driver-code-text"}>
            {manifestDetailsAssignmentState.selectedManifest?.driver?.code ?? ""}
          </Text>
        </DriverCode>
        {/* Card header details */}
        <GridHeaderContainer>
          <ManifestCardItemContainer>
            <Text style={{color: "#797979"}}>
              {manifestDetailsAssignmentState.selectedManifest?.driver.name?.toUpperCase()}
            </Text>
            <PipeIcon size="lg" icon={thin("pipe")}></PipeIcon>
            <VehicleSelector
              items={getArrayFromField(vehicleTypesResults?.data?.searchVehicleTypes?.items)}
              selectedItem={getArrayFromField(vehicleTypesResults?.data?.searchVehicleTypes?.items).find(
                (vt) => vt.vehicleTypeId === selectedVehicleTypeId
              )}
              onItemSelect={onUpdateManifestVehicle}
            />
            <PipeIcon size="lg" icon={thin("pipe")}></PipeIcon>
            <Popover
              minimal
              placement="bottom-start"
              isOpen={isOpenDispatchGroupSelector}
              onClose={() => setIsOpenDispatchGroupSelector(false)}
              canEscapeKeyClose
              content={
                <DispatchGroupsSeletor
                  initGroup={{
                    key: manifestDetailsAssignmentState.selectedManifest?.dispatchGroup?.dispatchGroupId as number,
                    value:
                      manifestDetailsAssignmentState.selectedManifest?.dispatchGroup?.name ?? EmptyValueStrings.unknown
                  }}
                  options={dispatchGroupOptions}
                  selectedGroup={selectedGroup}
                  onSelectGroup={onSelectGroupHandler}
                  originalGroupId={mappedDriverInfo?.dispatchGroupId as number}
                  originalText={isDispatchGroupChanged ? "original" : ""}
                  maxHeight={"50vh"}
                  maxWidth={"190px"}
                />
              }
              modifiers={DEFAULT_POPOVER_MODIFIERS}
            >
              <DetailButton
                data-testid="manifest-dispatch-group"
                variant="minimal"
                endIcon="chevron-down"
                onClick={() => setIsOpenDispatchGroupSelector((prev) => !prev)}
              >
                {selectedGroup?.value ??
                  manifestDetailsAssignmentState.selectedManifest?.dispatchGroup?.name ??
                  EmptyValueStrings.unknown}
                {isDispatchGroupChanged && (
                  <span style={{marginLeft: "3px"}}>
                    <Tooltip
                      popoverClassName="white-popover"
                      position="top"
                      content={`Changed from '${
                        mappedDriverInfo?.dispatchGroup?.dispatchGroup ?? EmptyValueStrings.unknown
                      }'`}
                    >
                      <SwapIcon />
                    </Tooltip>
                  </span>
                )}
              </DetailButton>
            </Popover>
            <PipeIcon size="lg" icon={thin("pipe")}></PipeIcon>
            <ManifestStatusSeletor selectedStatus={selectedStatus} onSelectStatus={onUpdateManifestStatus} />
            <PipeIcon size="lg" icon={thin("pipe")}></PipeIcon>
            <Text style={{color: "#797979"}}>{manifestDetailsAssignmentState.selectedManifest?.manifestDriverId}</Text>
            <PipeIcon size="lg" icon={thin("pipe")}></PipeIcon>
            <StopCountIndicator
              stops={manifestDetailsAssignmentState.selectedManifest?.stops}
              indicatorColor={highestRankIndicatorColor}
            />
            {(manifestDetailsAssignmentState.selectedManifest?.driver.qualifications ?? []).length > 0 && (
              <>
                <PipeIcon size="lg" icon={thin("pipe")}></PipeIcon>
                <DriverQualV3
                  qualifications={manifestDetailsAssignmentState.selectedManifest?.driver.qualifications ?? []}
                />
              </>
            )}
            {getActiveDriverSkills(manifestDetailsAssignmentState?.selectedManifest?.driver?.skills ?? [], skills)
              .length > 0 && (
              <>
                <PipeIcon size="lg" icon={thin("pipe")}></PipeIcon>
                <DriverQualV3
                  qualifications={getActiveDriverSkills(
                    manifestDetailsAssignmentState?.selectedManifest?.driver?.skills ?? [],
                    skills
                  )}
                />
              </>
            )}
          </ManifestCardItemContainer>
          <ManifestCardItemContainer>
            {(hasPendingSequenceUpdates || isTestingEnvironment) && (
              <SaveChangesContainer>
                <ManifestDateContainerChangesPending>{formattedManifestDate()}</ManifestDateContainerChangesPending>
                <SaveChangesButton onClick={onSaveChangesClicked} data-testid={"manifest-resequence-save-button"}>
                  Save Changes
                </SaveChangesButton>
                <CancelButton minimal={true}>
                  <CancelButtonText onClick={resetManifestStops}>Cancel</CancelButtonText>
                </CancelButton>
              </SaveChangesContainer>
            )}
            {["ASSIGNDRIVER", "SPLITJOB"].includes(manifestDetailsAssignmentState.detailsViewType) && (
              <CancelReassignmentButton minimal={true} onClick={onCancelReassignment}>
                {manifestDetailsAssignmentState.detailsViewType === "SPLITJOB"
                  ? "Cancel Split Job"
                  : "Cancel Reassignment"}
              </CancelReassignmentButton>
            )}
            <ManifestDateAndConfigButton>
              {!hasPendingSequenceUpdates &&
                !["ASSIGNDRIVER", "SPLITJOB"].includes(manifestDetailsAssignmentState.detailsViewType) && (
                  <ManifestDateContainer>{formattedManifestDate()}</ManifestDateContainer>
                )}
              <Button
                data-testid="manifest-detail-tool-panel"
                variant="minimal"
                title={"Columns"}
                icon={<FontAwesomeIcon style={{width: "16px", height: "14px"}} icon={solid("line-columns")} />}
                onClick={() => {
                  openOrCloseToolPanel("columns");
                }}
              />
            </ManifestDateAndConfigButton>
          </ManifestCardItemContainer>
        </GridHeaderContainer>
        <GridContainer className={"ag-theme-alpine"}>
          <StopsGrid
            context={
              {
                selectedStops: selectedStops,
                manifest: manifestDetailsAssignmentState.selectedManifest,
                handleUnassignJob: handleUnassignJobClicked,
                handleMarkJobStopComplete: setMarkCompletedJobStopId,
                handleMarkJobStopIncomplete: setMarkIncompleteJobStopId,
                handleReassignJob: handleReassignJobClicked,
                handleAdjustTimeJob: setAdjustStops,
                handleSplitJob,
                canUnassignPartialJob,
                isSplitJobEnabled,
                backendColorRulesEnabled,
                handleSnoozeIndicator
              } as ManifestDetailsContext
            }
            getRowId={(params) => "" + params.data.jobStopId}
            rowData={displayedStops}
            rowDragManaged={false}
            rowDragMultiRow={true}
            onRowDragLeave={onRowDragLeave}
            rowSelection={"multiple"}
            onRowSelected={onRowSelected}
            rowMultiSelectWithClick={true}
            onFilterChanged={onFilterChange}
            onRowDragEnd={onDragEnd}
            onRowDragMove={onDragging}
            onRowDragEnter={onRowDragEnter}
            columnDefs={colDefs}
            rowHeight={52}
            headerHeight={40}
            ref={gridRef}
            defaultColDef={defaultColDef}
            animateRows={true}
            overlayLoadingTemplate={`Loading manifest ${manifestDriverId}...`}
            noRowsOverlayComponent={() => (
              <ManifestDetailsNoRowsOverlay
                hasTooManyStops={exceedMaximumManifest}
                maxStopCount={getMaxStopCount(manifestDetailsAssignmentState.selectedManifest)}
              />
            )}
            getContextMenuItems={ManifestDetailsContextMenu}
            onFirstDataRendered={onFirstDataRendered}
            onGridReady={handleGridReady}
            onSortChanged={onColumnSortChanged}
            onColumnMoved={onColumnMoveChanged}
            onColumnResized={onColumnMoveChanged}
            onColumnVisible={onColumnVisibleChanged}
            maintainColumnOrder
            suppressDragLeaveHidesColumns
            sideBar={sideBar}
            columnTypes={columnTypes}
            floatingFiltersHeight={18}
            isShowFloatFilter={false}
            suppressMultiSort={!multipleSortEnabled}
          />
        </GridContainer>
        {manifestDetailsAssignmentState.detailsViewType === "ASSIGNDRIVER" && (
          <AssignmentBarManifestDetails
            selectedManifest={selectedManifest}
            selectedRows={selectedStops}
            assignText="REASSIGN"
            isEnableQualified={isEnableQualified}
            onAssign={handleReassignJobToManifest}
            onClearSelection={clearAllSelection}
            onQualify={handleToggleQualified}
          />
        )}
        {manifestDetailsAssignmentState.detailsViewType === "SPLITJOB" && (
          <AssignmentBarManifestDetails
            selectedManifest={selectedManifest}
            selectedRows={selectedStops}
            assignText="ASSIGN"
            isEnableQualified={false}
            hideQualifiedDrivers={true}
            onAssign={handleSplitJobAssignClicked}
            onClearSelection={clearAllSelection}
            onQualify={handleToggleQualified}
            disabled={selectedStops.length === 0 || splitJobDriver === undefined}
          />
        )}
      </ManifestDriverDetailsContainer>

      <ManifestDriverDetailsToolsContainer data-testid="manifest-details-tools-container">
        {manifestDetailsAssignmentState.detailsViewType === "MAP" && (
          <ManifestDetailsMap
            mapJobStop={selectedJobStopIds}
            manifest={manifestDetailsAssignmentState.selectedManifest}
            optimizedStops={workingStops}
            driverLocationResult={driverLocationResult}
            isDraggedStops={isDraggedStops}
            isOptimized={isOptimized}
            optimizedInfo={optimizedInfo}
            optimizedResultCords={optimizedResultCords}
          />
        )}
        {manifestDetailsAssignmentState.detailsViewType === "ASSIGNDRIVER" && assignmentManifestList}
        {manifestDetailsAssignmentState.detailsViewType === "SPLITJOB" && splitDriverList}
      </ManifestDriverDetailsToolsContainer>
    </ManifestDriverDetailsWrapper>
  );
};

export default ManifestDetailsV2;
